import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, Inject, Input, Optional, ViewChild, inject } from '@angular/core';
import { ErrorService } from '../../shared/error.service';
import { forkJoin, map, Observable, of, catchError, switchMap, tap, take, shareReplay } from 'rxjs';
import { CodeableConceptField, CodeableConceptFieldFromBackEnd, fieldType, FormFields, generalFieldsData, IndividualField, IndividualReferenceField, SingleCodeField } from '../../shared/dynamic-forms.interface2';
import { FormFieldsSelectDataService } from '../../shared/form-fields-select-data.service';
import { UtilityService } from '../../shared/utility.service';
import { commonImports } from '../../shared/table-interface';
import { MatStepperModule } from '@angular/material/stepper';
import { DynamicFormsV2Component } from "../../shared/dynamic-forms-v2/dynamic-forms-v2.component";
import { ResourceDataReviewComponent } from '../../shared/resource-data-review/resource-data-review.component';
import { ReferenceDisplayComponent } from "../../shared/reference-display/reference-display.component";
import { CodeableReferenceDisplayComponent } from "../../shared/codeable-reference-display/codeable-reference-display.component";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TabledOptionComponent } from '../../tabled-option/tabled-option.component';
import { backendEndPointToken } from '../../app.config';
import { Attachment, Bundle, ChargeItem, DiagnosticReport, FhirResource, ImagingStudy, Invoice, Observation, Practitioner, Reference, ServiceRequest, Specimen, SupplyDelivery } from 'fhir/r4';
import { InfoDialogService } from '../../shared/info-dialog/info-dialog.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FhirResourceTransformService } from '../../shared/fhir-resource-transform.service';
import { ImagingStudyImagesComponent } from '../../shared/imaging-study-images/imaging-study-images.component';
import { ImagingStudyLocalImage, selectedImagesToDiagnosticReportPresentedForms } from '../../shared/imaging-study-images/imaging-study-images.util';
import { StateService } from '../../shared/state.service';
import { SelectedObsComponent } from '../../diagnostic-report/selected-obs/selected-obs.component';
import { SelectedSpecimensComponent } from '../selected-specimens/selected-specimens.component';
import { ConfirmDialogComponent } from '../../shared/confirm-dialog/confirm-dialog.component';
import { FinalizeDiagnosticReportConfirmDialogComponent } from '../../shared/finalize-diagnostic-report-confirm-dialog/finalize-diagnostic-report-confirm-dialog.component';
import { ItemsRequiredApplyDialogComponent, ItemsRequiredApplyChoice } from '../../shared/items-required-apply-dialog/items-required-apply-dialog.component';
import { applyItemsRequiredRowsToActivityDefinition, extractItemsRequiredRows, ItemsRequiredRow } from '../../shared/activity-definition-items-required';
import { LabBillingPlaceholderService } from '../../shared/services/lab-billing-placeholder.service';
import { ServiceRequestBillingPlaceholderService } from '../../shared/services/service-request-billing-placeholder.service';
import { JsonPipe } from '@angular/common';
import { AuthService, User } from '../../shared/auth/auth.service';
import { DiagnosticReportDetailsComponent } from '../../diagnostic-report/diagnostic-report-details/diagnostic-report-details.component';
import { SelectedImagingStudiesComponent } from '../../diagnostic-report/selected-imaging-studies/selected-imaging-studies.component';
import { UploadUiComponent } from '../../upload-ui/upload-ui.component';
import { FirebaseAlertService } from '../../shared/services/firebase-alert.service';
import { inferServiceRequestType } from '../../shared/service-request-type.util';
// import e from 'express';

@Component({
  selector: 'app-add-report',
  imports: [...commonImports,
    SelectedObsComponent,
    SelectedImagingStudiesComponent,
    SelectedSpecimensComponent, JsonPipe,
    MatStepperModule, DynamicFormsV2Component, ResourceDataReviewComponent, ReferenceDisplayComponent, CodeableReferenceDisplayComponent, ImagingStudyImagesComponent,
    UploadUiComponent,
    DiagnosticReportDetailsComponent],
  templateUrl: './add-report.component.html',
  styleUrl: './add-report.component.scss'
})
export class AddReportComponent {
  @ViewChild(DynamicFormsV2Component) private formCmp?: DynamicFormsV2Component;

  diagnosticReportUiMode: 'form' | 'review' = 'form';
  private diagnosticReportFormValues: any = null;
  // backendApiBaseUrl = inject(backendEndPointToken);

  @Input() serviceRequest?: ServiceRequest;
  @Input() serviceRequests?: ServiceRequest[];
  @Input() patientId?: string;

  private readonly http = inject(HttpClient);
  private readonly backendApiBaseUrl = inject(backendEndPointToken);
  private readonly infoDialog = inject(InfoDialogService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly fhirTransformService = inject(FhirResourceTransformService);
  private readonly stateService = inject(StateService);
  private readonly billingPlaceholder = inject(LabBillingPlaceholderService);
  private readonly serviceRequestBillingPlaceholder = inject(ServiceRequestBillingPlaceholderService);
  private readonly auth = inject(AuthService);
  private readonly dialogRef = inject(MatDialogRef<AddReportComponent>, { optional: true });
  private readonly dialogData = inject<any>(MAT_DIALOG_DATA, { optional: true });
  private readonly firebaseAlertService = inject(FirebaseAlertService);

  private normalizeNameToken(value: string): string {
    return (value ?? '')
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[^a-z\-\s']/g, '')
      .replace(/\s+/g, ' ');
  }

  private readonly practitionerLastNameById = new Map<string, Observable<string | null>>();

  private resolveLoggedInPractitionerId(): string | null {
    const user: User | null = this.auth.user.getValue();
    const id = (user?.fhirResourceId ?? '').toString().trim();
    return id || null;
  }

  private extractLastNameFromPractitioner(pr: Practitioner | any): string | null {
    const names: any[] = Array.isArray(pr?.name) ? pr.name : [];
    const first = names[0] ?? null;
    if (!first) return null;

    const fam = (first?.family ?? '').toString().trim();
    if (fam) return fam;

    const text = (first?.text ?? '').toString().trim();
    if (text) {
      const parts = text.split(/\s+/).filter(Boolean);
      return parts.length ? parts[parts.length - 1] : null;
    }

    const given: any[] = Array.isArray(first?.given) ? first.given : [];
    // Some servers may store full name in given[0]
    if (given.length) {
      const maybeFull = String(given[given.length - 1] ?? '').trim();
      if (maybeFull) {
        const parts = maybeFull.split(/\s+/).filter(Boolean);
        return parts.length ? parts[parts.length - 1] : maybeFull;
      }
    }

    return null;
  }

  private resolvePractitionerLastNameFromBackend$(practitionerId: string): Observable<string | null> {
    const id = (practitionerId ?? '').toString().trim();
    if (!id) return of(null);

    if (this.practitionerLastNameById.has(id)) {
      return this.practitionerLastNameById.get(id)!;
    }

    const req$ = this.http.get<Practitioner>(`${this.backendApiBaseUrl}/Practitioner/${id}`).pipe(
      map((pr) => this.extractLastNameFromPractitioner(pr)),
      catchError((e: any) => {
        console.error('Failed to resolve Practitioner last name from backend', e);
        return of(null);
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.practitionerLastNameById.set(id, req$);
    return req$;
  }

  private resolveLoggedInUserLastName(): string | null {
    const user: any = this.auth.user.getValue();
    if (!user) return null;

    const nameArray = user?.name;
    if (Array.isArray(nameArray) && nameArray.length) {
      const fam = (nameArray?.[0]?.family ?? '').toString().trim();
      if (fam) return fam;
      const full = (nameArray?.[0]?.text ?? '').toString().trim();
      if (full) {
        const parts = full.split(/\s+/).filter(Boolean);
        return parts.length ? parts[parts.length - 1] : null;
      }
    }

    const display = (user?.display ?? user?.fullName ?? user?.name ?? '').toString().trim();
    if (!display) return null;
    const parts = display.split(/\s+/).filter(Boolean);
    return parts.length ? parts[parts.length - 1] : null;
  }

  private hasCurrentPatientInState(): boolean {
    const id = this.stateService.PatientResources.currentPatient.value?.actualResource?.id ?? null;
    return !!id;
  }

  private extractSavedDiagnosticReportFromTransactionResponse(resp: any, fallbackId?: string | null): DiagnosticReport | null {
    const entries = Array.isArray(resp?.entry) ? resp.entry : [];
    const resources = entries.map((e: any) => e?.resource).filter(Boolean);
    const drs = resources.filter((r: any) => r?.resourceType === 'DiagnosticReport') as DiagnosticReport[];
    if (!drs.length) return null;
    if (fallbackId) {
      const byId = drs.find((d: any) => (d?.id ?? '').toString() === fallbackId);
      if (byId) return byId as any;
    }
    return drs[0] as any;
  }

  private saveFinalReportAndCompleteServiceRequestsAsBundle$(
    report: DiagnosticReport,
    serviceRequestIds: string[],
    existingDiagnosticReportId: string | null
  ): Observable<DiagnosticReport | null> {
    const srIds = Array.from(new Set((serviceRequestIds ?? []).map((id) => (id ?? '').toString().trim()).filter(Boolean)));
    if (!srIds.length) {
      // Still allow finalization even if no SR ids resolved.
      srIds.push();
    }

    const headers = { 'Content-Type': 'application/fhir+json', Accept: 'application/fhir+json', Prefer: 'return=representation' };

    // Important: fetch fresh SRs to avoid overwriting fields that may have been updated earlier in this flow
    // (e.g. instantiatesCanonical pinning for audit).
    const fetchSrs$ = srIds.length
      ? forkJoin(
        srIds.map((id) =>
          this.http.get<ServiceRequest>(`${this.backendApiBaseUrl}/ServiceRequest/${encodeURIComponent(id)}`, { headers: { Accept: 'application/fhir+json' } }).pipe(
            catchError(() => of(null as any))
          )
        )
      ).pipe(map((list) => (list || []).filter(Boolean) as ServiceRequest[]))
      : of([] as ServiceRequest[]);

    return fetchSrs$.pipe(
      switchMap((freshSrs) => {
        const entries: any[] = [];

        const cleanedReport = this.cleanFhirResourceForSubmit({
          ...(report as any),
          ...(existingDiagnosticReportId ? { id: existingDiagnosticReportId } : {}),
        } as any);

        entries.push({
          request: existingDiagnosticReportId
            ? { method: 'PUT', url: `DiagnosticReport/${existingDiagnosticReportId}` }
            : { method: 'POST', url: 'DiagnosticReport' },
          resource: cleanedReport,
        });

        for (const sr of freshSrs) {
          const id = (sr?.id ?? '').toString().trim();
          if (!id) continue;
          const next: any = { ...sr, status: 'completed' };
          entries.push({
            request: { method: 'PUT', url: `ServiceRequest/${id}` },
            resource: this.cleanFhirResourceForSubmit(next),
          });
        }

        // Best-effort inventory decrement: apply itemsRequiredRows to SupplyDelivery.totalRemaining.
        // This uses org-wide SupplyDeliveries already loaded into state.
        try {
          const currentDeliveries = (this.stateService.orgWideResources.supplyDeliveries.getValue() ?? [])
            .map((w: any) => w?.actualResource)
            .filter(Boolean) as SupplyDelivery[];
          const updated = this.applyItemsRequiredUsageToSupplyDeliveries(currentDeliveries, this.itemsRequiredRows);

          for (const sd of updated) {
            const id = (sd as any)?.id?.toString?.() ?? '';
            if (!id) continue;
            entries.push({
              request: { method: 'PUT', url: `SupplyDelivery/${id}` },
              resource: this.cleanFhirResourceForSubmit(sd as any),
            });
          }
        } catch {
          // ignore; inventory decrement should not block finalization
        }

        const bundle: Bundle = { resourceType: 'Bundle', type: 'transaction', entry: entries };
        return this.http.post<Bundle>(this.backendApiBaseUrl, bundle, { headers }).pipe(
          tap((resp: any) => {
            if (!resp) return;
            // Always persist org-wide.
            this.stateService.processOrgWideBundleTransaction(resp as any);
            // Only persist patient-scoped if a current patient exists in state.
            if (this.hasCurrentPatientInState()) {
              this.stateService.processBundleTransaction(resp as any);
            }

            // ✅ Post alerts to original requesters
            const orgId = this.auth.user.getValue()?.organizationId || this.auth.user.getValue()?.hospitalId;
            if (orgId && freshSrs.length) {
              const patientId = this.stateService.currentPatientId$.getValue();
              const patientName = this.stateService.PatientResources.currentPatient.getValue()?.actualResource?.name?.[0]?.text || 'Patient';

              freshSrs.forEach(sr => {
                const requesterRef = sr.requester?.reference;
                if (requesterRef && requesterRef.includes('Practitioner/')) {
                  const requesterId = requesterRef.split('/')[1];
                  const alertPayload: any = {
                    title: 'Diagnostic Report Ready',
                    description: `A report is ready for ${patientName}'s request: ${sr.code?.text || 'Service'}`,
                    url: `/patient-wrapper/${patientId}/diagnostic-reports`
                  };
                  if (report?.id) alertPayload.query = { resourceId: report.id };
                  this.firebaseAlertService.postUserAlert(orgId, requesterId, alertPayload).subscribe();
                }
              });
            }
          }),
          catchError((e: any) => {
            console.error(e);
            this.errorService.openandCloseError('Failed to finalize DiagnosticReport and complete ServiceRequests (transaction failed).');
            return of(null as any);
          })
        );
      }),
      map((resp: any) => {
        if (!resp) return null;
        return this.extractSavedDiagnosticReportFromTransactionResponse(resp, existingDiagnosticReportId);
      }),
      catchError(() => of(null))
    );
  }

  private readonly supplyDeliveryExtensionUrls = {
    totalRemaining: `${this.backendApiBaseUrl.replace(/\/+$/, '')}/StructureDefinition/totalRemaining`,
    packItemRemaining: `${this.backendApiBaseUrl.replace(/\/+$/, '')}/StructureDefinition/packItemRemaining`,
    packageDetails: `${this.backendApiBaseUrl.replace(/\/+$/, '')}/StructureDefinition/packageDetails`,
  } as const;

  private getSupplyDeliveryTotalRemaining(sd: any): { value: number | null; unit?: string } {
    const exts: any[] = Array.isArray(sd?.extension) ? sd.extension : [];
    const ext = exts.find((e) => (e?.url ?? '') === this.supplyDeliveryExtensionUrls.totalRemaining
      || (typeof e?.url === 'string' && e.url.endsWith('/totalRemaining')));
    const unit = ext?.valueQuantity?.unit ?? (sd?.suppliedItem?.quantity?.unit ?? undefined);
    const raw = ext?.valueQuantity?.value ?? ext?.valueInteger ?? null;
    const n = raw === null || raw === undefined || raw === '' ? null : Number(raw);
    if (n === null || !Number.isFinite(n)) return { value: null, unit };
    return { value: n, unit };
  }

  private setSupplyDeliveryTotalRemaining(sd: any, value: number, unit?: string): void {
    if (!sd) return;
    if (!Array.isArray(sd.extension)) sd.extension = [];
    const exts: any[] = sd.extension;
    let ext = exts.find((e) => (e?.url ?? '') === this.supplyDeliveryExtensionUrls.totalRemaining
      || (typeof e?.url === 'string' && e.url.endsWith('/totalRemaining')));
    if (!ext) {
      ext = { url: this.supplyDeliveryExtensionUrls.totalRemaining, valueQuantity: {} };
      exts.push(ext);
    }
    ext.url = this.supplyDeliveryExtensionUrls.totalRemaining;
    ext.valueQuantity = {
      value,
      ...(unit ? { unit } : {})
    };
    delete ext.valueInteger;
  }

  private getQuantityPerPackage(sd: any): number | null {
    const exts: any[] = Array.isArray(sd?.extension) ? sd.extension : [];
    const pkg = exts.find((e) => (e?.url ?? '') === this.supplyDeliveryExtensionUrls.packageDetails
      || (typeof e?.url === 'string' && e.url.endsWith('/packageDetails')));
    const inner: any[] = Array.isArray(pkg?.extension) ? pkg.extension : [];
    const qpp = inner.find((e) => (e?.url ?? '') === 'quantityPerPackage');
    const raw = qpp?.valueQuantity?.value;
    const n = raw === null || raw === undefined || raw === '' ? null : Number(raw);
    if (n === null || !Number.isFinite(n) || n <= 0) return null;
    return n;
  }

  private setPackItemRemaining(sd: any, value: number): void {
    if (!sd) return;
    if (!Array.isArray(sd.extension)) sd.extension = [];
    const exts: any[] = sd.extension;
    let ext = exts.find((e) => (e?.url ?? '') === this.supplyDeliveryExtensionUrls.packItemRemaining
      || (typeof e?.url === 'string' && e.url.endsWith('/packItemRemaining')));
    if (!ext) {
      ext = { url: this.supplyDeliveryExtensionUrls.packItemRemaining, valueInteger: value };
      exts.push(ext);
      return;
    }
    ext.url = this.supplyDeliveryExtensionUrls.packItemRemaining;
    ext.valueInteger = Math.max(0, Math.trunc(value));
    delete ext.valueQuantity;
  }

  private applyItemsRequiredUsageToSupplyDeliveries(
    deliveries: SupplyDelivery[],
    rows: ItemsRequiredRow[]
  ): SupplyDelivery[] {
    const list = deliveries ?? [];
    if (!list.length) return [];

    const usageByDeliveryId = new Map<string, number>();

    const normalize = (s: any) => (s ?? '').toString().trim().toLowerCase().replace(/\/+$/, '');
    const codingKey = (coding: any) => {
      const system = normalize(coding?.system);
      const code = normalize(coding?.code);
      return system && code ? `${system}::${code}` : '';
    };

    const matchDelivery = (row: ItemsRequiredRow): SupplyDelivery | null => {
      const item: any = (row as any)?.items;
      const rowCoding = Array.isArray(item?.coding) ? item.coding[0] : null;
      const rowKey = codingKey(rowCoding);
      const rowText = normalize(item?.text ?? rowCoding?.display ?? rowCoding?.code);

      // Prefer system+code match.
      if (rowKey) {
        const found = list.find((sd: any) => {
          const sdCoding = sd?.suppliedItem?.itemCodeableConcept?.coding?.[0];
          return codingKey(sdCoding) === rowKey;
        });
        if (found) return found;
      }

      // Fallback: match by text/display.
      if (rowText) {
        const found = list.find((sd: any) => {
          const cc: any = sd?.suppliedItem?.itemCodeableConcept;
          const sdCoding = cc?.coding?.[0];
          const sdText = normalize(cc?.text ?? sdCoding?.display ?? sdCoding?.code);
          return !!sdText && sdText === rowText;
        });
        if (found) return found;
      }

      return null;
    };

    for (const row of rows ?? []) {
      const delivery = matchDelivery(row);
      const id = (delivery as any)?.id?.toString?.() ?? '';
      if (!delivery || !id) continue;

      const qRaw: any = (row as any)?.quantity;
      const qNum = qRaw === null || qRaw === undefined || qRaw === '' ? 1 : Number(qRaw);
      const qty = Number.isFinite(qNum) && qNum > 0 ? qNum : 0;
      if (!qty) continue;

      usageByDeliveryId.set(id, (usageByDeliveryId.get(id) ?? 0) + qty);
    }

    const updated: SupplyDelivery[] = [];
    for (const [id, usedQty] of usageByDeliveryId.entries()) {
      const original = list.find((sd: any) => (sd?.id ?? '').toString() === id);
      if (!original) continue;

      const next: any = structuredClone(original);
      const remaining = this.getSupplyDeliveryTotalRemaining(next);
      const baseValue = remaining.value;
      if (baseValue === null) continue;

      const newRemaining = Math.max(0, baseValue - usedQty);
      this.setSupplyDeliveryTotalRemaining(next, newRemaining, remaining.unit);

      // Optional: keep packItemRemaining aligned to remaining stock if package sizing is provided.
      const qpp = this.getQuantityPerPackage(next);
      if (qpp && Number.isFinite(qpp) && qpp > 0) {
        const packCount = Math.max(0, Math.ceil(newRemaining / qpp));
        this.setPackItemRemaining(next, packCount);
      }

      updated.push(next as SupplyDelivery);
    }

    return updated;
  }

  private normalizeServiceRequestIdFromReference(reference: any): string | null {
    const raw = (reference ?? '').toString().trim();
    if (!raw) return null;
    // Handles:
    // - ServiceRequest/{id}
    // - {base}/ServiceRequest/{id}
    // - .../ServiceRequest/{id}?...
    const match = raw.match(/ServiceRequest\/([^\/#\?]+)/i);
    return match?.[1] ?? null;
  }

  private diagnosticReportMatchesServiceRequestIds(dr: any, srIds: string[], strictEquality = true): boolean {
    const wanted = (srIds ?? []).map((s) => (s ?? '').toString().trim()).filter(Boolean);
    if (!wanted.length) return false;

    const basedOn = Array.isArray(dr?.basedOn) ? dr.basedOn : [];
    const have = new Set(
      basedOn
        .map((b: any) => this.normalizeServiceRequestIdFromReference(b?.reference))
        .filter(Boolean) as string[]
    );

    const allPresent = wanted.every((id) => have.has(id));
    if (!allPresent) return false;
    if (!strictEquality) return true;
    return have.size === wanted.length;
  }

  private reportEditableStatus(status: any): boolean {
    const st = (status ?? '').toString().trim().toLowerCase();
    return !['cancelled', 'entered-in-error', 'unknown', 'final'].includes(st);
  }

  private extractReportSortInstant(dr: any): number {
    const candidate =
      (dr as any)?.meta?.lastUpdated ??
      (dr as any)?.issued ??
      (dr as any)?.effectiveDateTime ??
      (dr as any)?.date ??
      null;
    const t = candidate ? Date.parse(candidate.toString()) : NaN;
    return Number.isFinite(t) ? t : 0;
  }

  private pickPreferredDiagnosticReport(list: DiagnosticReport[], srIds: string[], strict: boolean): DiagnosticReport | null {
    const matches = (list ?? []).filter((dr: any) => this.diagnosticReportMatchesServiceRequestIds(dr, srIds, strict));
    if (!matches.length) return null;

    // Only FINAL is considered "non-editable but loadable".
    const finals = matches
      .filter((dr: any) => (dr?.status ?? '').toString().trim().toLowerCase() === 'final')
      .sort((a: any, b: any) => this.extractReportSortInstant(b) - this.extractReportSortInstant(a));
    if (finals.length) return finals[0] as any;

    // Otherwise load the most recent editable draft (registered/preliminary/partial/...)
    const drafts = matches
      .filter((dr: any) => this.reportEditableStatus(dr?.status))
      .sort((a: any, b: any) => this.extractReportSortInstant(b) - this.extractReportSortInstant(a));
    if (drafts.length) return drafts[0] as any;

    // Ignore cancelled / entered-in-error / unknown so they don't block starting a new report.
    return null;
  }

  private seedNewReportFromPrimary(primary: ServiceRequest): void {
    const seed: any = { resourceType: 'DiagnosticReport' };
    const srAny: any = primary as any;
    if (srAny?.code) seed.code = srAny.code;
    if (srAny?.category?.length) seed.category = srAny.category;
    seed.status = 'registered';

    this.existingReport = null;
    this.diagnosticReportWorking = seed;
    this.diagnosticReportUiMode = 'form';

    this.selectedObservationRefs = [];
    this.selectedImagingStudyRefs = [];
    this.selectedSpecimenRefs = [];
    this.selectedSpecimensV2 = [];
    this.puttedObservationsV2 = [];
    this.puttedImagingStudiesV2 = [];
  }

  private fetchReferencedObservations$(dr: any): Observable<Observation[]> {
    const refs = (Array.isArray(dr?.result) ? dr.result : []) as any[];
    const ids = refs
      .map((r: any) => (r?.reference ?? '').toString().trim())
      .map((ref) => ref.split('?')[0])
      .map((ref) => ref.match(/Observation\/([^\/#\?]+)/i)?.[1] ?? ref.split('/').pop())
      .filter(Boolean) as string[];

    if (!ids.length) return of([]);
    return forkJoin(
      ids.map((id) =>
        this.http.get<Observation>(`${this.backendApiBaseUrl}/Observation/${id}`).pipe(
          catchError(() => of(null as any))
        )
      )
    ).pipe(map((list) => (list || []).filter(Boolean) as Observation[]));
  }

  private fetchReferencedImagingStudies$(dr: any): Observable<ImagingStudy[]> {
    const imagingRefs = (Array.isArray((dr as any)?.imagingStudy) ? (dr as any).imagingStudy : []) as any[];
    const legacyResultRefs = (Array.isArray(dr?.result) ? dr.result : []) as any[];
    const refs = imagingRefs.length
      ? imagingRefs
      : legacyResultRefs.filter((r: any) => ((r?.reference ?? '').toString().trim().toLowerCase().includes('imagingstudy/')));
    const ids = refs
      .map((r: any) => (r?.reference ?? '').toString().trim())
      .map((ref) => ref.split('?')[0])
      .map((ref) => ref.match(/ImagingStudy\/([^\/#\?]+)/i)?.[1] ?? ref.split('/').pop())
      .filter(Boolean) as string[];

    if (!ids.length) return of([]);
    return forkJoin(
      ids.map((id) =>
        this.http.get<ImagingStudy>(`${this.backendApiBaseUrl}/ImagingStudy/${id}`).pipe(
          catchError(() => of(null as any))
        )
      )
    ).pipe(map((list) => (list || []).filter(Boolean) as ImagingStudy[]));
  }

  private fetchReferencedSpecimens$(dr: any): Observable<Specimen[]> {
    const refs = (Array.isArray(dr?.specimen) ? dr.specimen : []) as any[];
    const ids = refs
      .map((r: any) => (r?.reference ?? '').toString().trim())
      .map((ref) => ref.split('?')[0])
      .map((ref) => ref.match(/Specimen\/([^\/#\?]+)/i)?.[1] ?? ref.split('/').pop())
      .filter(Boolean) as string[];

    if (!ids.length) return of([]);
    return forkJoin(
      ids.map((id) =>
        this.http.get<Specimen>(`${this.backendApiBaseUrl}/Specimen/${id}`).pipe(
          catchError(() => of(null as any))
        )
      )
    ).pipe(map((list) => (list || []).filter(Boolean) as Specimen[]));
  }

  private applyExistingDiagnosticReportToState(primary: ServiceRequest, dr: DiagnosticReport): void {
    this.existingReport = dr;
    this.syncDialogSizeForFinalReport(dr);

    this.isEditableV2 = this.reportEditableStatus((dr as any)?.status);
    this.diagnosticReportWorking = structuredClone(dr as any);
    this.diagnosticReportUiMode = 'review';

    this.normalizeDiagnosticReportWorkingPresentedForms();

    // Seed refs for saveReport()
    const res = (Array.isArray((dr as any)?.result) ? (dr as any).result : []) as any[];
    this.selectedObservationRefs = res
      .filter((r: any) => ((r?.reference ?? '').toString().trim().toLowerCase().includes('observation/')))
      .map((r: any) => ({ reference: (r?.reference ?? '').toString(), display: r?.display } as Reference))
      .filter((r: any) => !!r?.reference);

    const imgRefsPreferred = (Array.isArray((dr as any)?.imagingStudy) ? (dr as any).imagingStudy : []) as any[];
    const imgRefsLegacy = res.filter((r: any) => ((r?.reference ?? '').toString().trim().toLowerCase().includes('imagingstudy/')));
    const imgRefs = [...imgRefsPreferred, ...imgRefsLegacy]
      .map((r: any) => ({ reference: (r?.reference ?? '').toString(), display: r?.display } as Reference))
      .filter((r: any) => !!r?.reference);
    {
      const byRef = new Map<string, Reference>();
      for (const r of imgRefs) {
        const k = (r?.reference ?? '').toString();
        if (k && !byRef.has(k)) byRef.set(k, r);
      }
      this.selectedImagingStudyRefs = Array.from(byRef.values());
    }

    const sp = (Array.isArray((dr as any)?.specimen) ? (dr as any).specimen : []) as any[];
    this.selectedSpecimenRefs = sp
      .map((r: any) => ({ reference: (r?.reference ?? '').toString(), display: r?.display } as Reference))
      .filter((r: any) => !!r?.reference);

    const categoryText = primary.category?.[0]?.coding?.[0]?.code ?? primary.category?.[0]?.text ?? 'unknown';
    const categoryLower = (categoryText ?? '').toString().toLowerCase();

    if (categoryLower.includes('imaging')) {
      this.toReferenceResultsV2 = 'ImagingStudy';
      this.fetchReferencedImagingStudies$(dr)
        .pipe(take(1))
        .subscribe((studies) => {
          this.puttedImagingStudiesV2 = studies;
        });
    } else if (categoryLower.includes('laboratory') || categoryLower.includes('vital')) {
      this.toReferenceResultsV2 = 'Observation';
      this.fetchReferencedObservations$(dr)
        .pipe(take(1))
        .subscribe((observations) => {
          this.puttedObservationsV2 = observations;
        });
    }

    this.fetchReferencedSpecimens$(dr)
      .pipe(take(1))
      .subscribe((specimens) => {
        // Default behavior: show whatever the report currently references.
        this.selectedSpecimensV2 = specimens;
      });
  }

  private syncDialogSizeForFinalReport(report: DiagnosticReport | null): void {
    const isFinal = (report?.status ?? '').toString().toLowerCase() === 'final';
    if (!isFinal) return;
// $#$// /* alert('This report is already finalized and cannot be edited. You may view it in read-only mode.'); */
    this.dialogRef?.updateSize('1100px', '95vh');
    
    this.dialogRef?.updatePosition({ top: '2vh' });
    this.dialogRef?.addPanelClass('add-report-dialog--expanded');
  }

  private initFromPrimaryServiceRequest(primary: ServiceRequest): void {
    if (!primary) return;

    // Keep component context in sync so saveReport()/loadContextAndReferences() can use it.
    this.serviceRequest = primary;

    const cat = primary.category?.[0]?.coding?.[0]?.code ?? primary.category?.[0]?.text ?? 'unknown';
    const catText = (cat ?? '').toString().toLowerCase();
    if (catText.includes('laboratory') || catText.includes('vital')) {
      this.toReferenceResultsV2 = 'Observation';
    } else if (catText.includes('imaging')) {
      this.toReferenceResultsV2 = 'ImagingStudy';
    }

    // Items Required step should be available before the DiagnosticReport form
    this.initItemsRequiredStep(primary);

    // Existing DiagnosticReport check to determine editability + preload results.
    const srId = (this.serviceRequestIdV2 ?? '').toString().trim();
    if (!srId) return;

    this.utilityService.threeTierQueryAndSyncToStateOnce$<DiagnosticReport>({
      resourceType: 'DiagnosticReport',
      urlSearchObject: {
        ...(!this.backendApiBaseUrl.includes('server.fire')
          ? { subject: `${primary.subject?.reference}` }
          : {}),
        'based-on': `ServiceRequest/${srId}`,
      },
      querySearchObject: [
        { key: 'subject.reference', value: `${primary.subject?.reference}` },

        {
          key: 'basedOn.reference',
          value: `${primary.subject?.reference?.includes('https') || primary.subject?.reference?.includes('http')
            ? this.backendApiBaseUrl + '/ServiceRequest/' + srId
            : 'ServiceRequest/' + srId}`
        }
      ]
    }).pipe(
      switchMap((f) => {
        if (!f || f.length === 0) {
          this.isEditableV2 = true;
          this.seedNewReportFromPrimary(primary);
          return of([] as DiagnosticReport[]);
        }

        // Single-SR view: only match DRs that are truly for this SR
        const srIds = [srId];
        const strict = true;
        const chosen = this.pickPreferredDiagnosticReport(f as any, srIds, strict);

        if (!chosen) {
          this.isEditableV2 = true;
          this.seedNewReportFromPrimary(primary);
          return of([] as DiagnosticReport[]);
        }

        this.applyExistingDiagnosticReportToState(primary, chosen as any);
        return of([] as DiagnosticReport[]);
      })
    ).subscribe();
  }

  closeSelf(): void {
    const confirmRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        message: "Are you sure you want to cancel creating this report? All progress will be lost.",
      }
    })

    confirmRef.afterClosed().subscribe((result) => {
      if (result) {
        this.dialogRef?.close();
      }
    });

  }

  raiseTd(ev: HTMLElement) {
    const tdReference = ev;
    const tds = document.getElementsByClassName('each-reference');
    Array.from(tds).forEach((f: any) => {
      f.style.zIndex = '1';
    });
    ev.style.zIndex = '2';
  }
  formFieldService = inject(FormFieldsSelectDataService);
  errorService = inject(ErrorService);
  labReportForm?: Map<string, {
    [key: string]: any,
    formFields: FormFields[];
  }>

  rawObservation?: any;
  rawSpecimen?: any;
  observation?: any;
  specimen?: any;

  // Items Required step state
  itemsRequiredForm?: FormFields[];
  itemsRequiredLoading = false;
  itemsRequiredSource: 'instance' | 'default' | 'none' = 'none';
  resolvedActivityDefinition: any = null;
  originalItemsRequiredRows: ItemsRequiredRow[] = [];
  itemsRequiredRows: ItemsRequiredRow[] = [];
  itemsRequiredDirty = false;
  itemsRequiredEditEnabled = true;
  itemsRequiredReadOnlyNote: string | null = null;

  // Audit enhancement: when saving a report, pin SRs that use the default ActivityDefinition
  // by setting ServiceRequest.instantiatesCanonical (so later default changes don't rewrite history).
  private readonly activityDefinitionType = 'ActivityDefinition';

  availableObservations: Observation[] = [];
  availableSpecimens: Specimen[] = [];
  selectedObservationRefs: Reference[] = [];
  selectedImagingStudyRefs: Reference[] = [];
  selectedSpecimenRefs: Reference[] = [];
  selectedSpecimensV2: Specimen[] = [];
  existingReport: DiagnosticReport | null = null;

  resolvedPatientIdV2: string | null = null;

  // Working DiagnosticReport instance edited through ResourceDataReview
  diagnosticReportWorking: any = { resourceType: 'DiagnosticReport' };

  // Local (non-cloud) image attachments for DiagnosticReport.presentedForm
  reportImages: ImagingStudyLocalImage[] = [];

  private readonly azureBlobNameExtensionUrl = 'https://elikita-server.daalitech.com/fhir/StructureDefinition/azureBlobName';

  get diagnosticReportFormFieldsWithoutAttachments(): FormFields[] {
    const fields = (this.toPassLabForm ?? []) as any[];
    if (!fields?.length) return [];
    return fields.filter((f: any) => (f?.generalProperties?.fieldApiName ?? '') !== 'attachment') as any;
  }

  private normalizeUploadUiFileToAttachment(file: any): Attachment | null {
    if (!file) return null;

    const looksLikeUploadUi = typeof file === 'object' && (file.fileLink || file.uploadFileName || file.azureBlobName);
    const looksLikeAttachment = typeof file === 'object' && (file.url || file.data || file.contentType || file.title);

    if (looksLikeAttachment && !looksLikeUploadUi) return file as Attachment;

    const url = (file.fileLink ?? file.url ?? '').toString().trim();
    const title = (file.uploadFileName ?? file.title ?? file.name ?? '').toString().trim();
    const contentType = (file.uploadFileType ?? file.contentType ?? file.type ?? '').toString().trim();
    const sizeRaw = file.uploadFileSize ?? file.size;
    const size = Number.isFinite(Number(sizeRaw)) ? Number(sizeRaw) : undefined;
    const creation = (file.uploadDate ?? file.creation ?? '').toString().trim();
    const azureBlobName = (file.azureBlobName ?? '').toString().trim();

    const attachment: Attachment = {
      ...(contentType ? { contentType } : {}),
      ...(url ? { url } : {}),
      ...(title ? { title } : {}),
      ...(size !== undefined ? { size } : {}),
      ...(creation ? { creation } : {}),
    } as any;

    if (azureBlobName) {
      (attachment as any).extension = [
        ...(((attachment as any).extension ?? []) as any[]),
        { url: this.azureBlobNameExtensionUrl, valueString: azureBlobName },
      ];
    }

    return attachment;
  }

  private extractAzureBlobNameFromAttachment(att: any): string | null {
    const ext = (att as any)?.extension;
    if (!Array.isArray(ext)) return null;
    const hit = ext.find((e: any) => (e?.url ?? '').toString() === this.azureBlobNameExtensionUrl);
    const v = (hit?.valueString ?? '').toString().trim();
    return v ? v : null;
  }

  private normalizeDiagnosticReportWorkingPresentedForms(): void {
    const pf = (this.diagnosticReportWorking as any)?.presentedForm;
    const list = (Array.isArray(pf) ? pf : (pf ? [pf] : [])) as any[];
    const normalized = list.map((x) => this.normalizeUploadUiFileToAttachment(x)).filter(Boolean);
    (this.diagnosticReportWorking as any).presentedForm = normalized;
  }

  get existingAttachmentFilesForUploadUi(): any[] {
    const pf = (this.diagnosticReportWorking as any)?.presentedForm;
    const list = (Array.isArray(pf) ? pf : (pf ? [pf] : [])) as any[];
    const normalized = list.map((x) => this.normalizeUploadUiFileToAttachment(x)).filter(Boolean) as Attachment[];

    return normalized
      .filter((a: any) => !!(a as any)?.url)
      .map((a: any) => ({
        fileLink: (a as any).url,
        uploadFileName: (a as any).title ?? (a as any).url,
        uploadFileType: (a as any).contentType ?? '',
        uploadFileSize: (a as any).size ?? undefined,
        uploadDate: (a as any).creation ?? undefined,
        azureBlobName: this.extractAzureBlobNameFromAttachment(a) ?? undefined,
      }));
  }

  onAttachmentFilesChangedFromUploadUi(files: any[]): void {
    // Preserve any non-URL attachments (e.g. base64 data) already in presentedForm.
    const pf = (this.diagnosticReportWorking as any)?.presentedForm;
    const currentList = (Array.isArray(pf) ? pf : (pf ? [pf] : [])) as any[];
    const currentNormalized = currentList.map((x) => this.normalizeUploadUiFileToAttachment(x)).filter(Boolean) as Attachment[];
    const preserved = currentNormalized.filter((a: any) => !(a as any)?.url);

    const nextUrlAttachments = (files ?? [])
      .map((f) => this.normalizeUploadUiFileToAttachment(f))
      .filter(Boolean)
      .filter((a: any) => !!(a as any)?.url) as Attachment[];

    const byUrl = new Map<string, Attachment>();
    for (const a of nextUrlAttachments) {
      const url = ((a as any)?.url ?? '').toString().trim();
      if (!url) continue;
      if (!byUrl.has(url)) byUrl.set(url, a);
    }

    (this.diagnosticReportWorking as any).presentedForm = [...preserved, ...Array.from(byUrl.values())];
  }

  updateAttachmentTitleByUrl(url: string, title: string): void {
    const pf = (this.diagnosticReportWorking as any)?.presentedForm;
    const list = (Array.isArray(pf) ? pf : (pf ? [pf] : [])) as any[];
    const next = list.map((x) => this.normalizeUploadUiFileToAttachment(x)).filter(Boolean) as Attachment[];
    for (const a of next as any[]) {
      if (((a?.url ?? '').toString().trim() === (url ?? '').toString().trim())) {
        (a as any).title = (title ?? '').toString();
      }
    }
    (this.diagnosticReportWorking as any).presentedForm = next;
  }

  updateAttachmentTitleAtIndex(index: number, title: string): void {
    const pf = (this.diagnosticReportWorking as any)?.presentedForm;
    const list = (Array.isArray(pf) ? pf : (pf ? [pf] : [])) as any[];
    const next = list.map((x) => this.normalizeUploadUiFileToAttachment(x)).filter(Boolean) as Attachment[];
    if (index < 0 || index >= next.length) return;
    (next[index] as any).title = (title ?? '').toString();
    (this.diagnosticReportWorking as any).presentedForm = next;
  }

  removeAttachmentByUrl(url: string): void {
    const pf = (this.diagnosticReportWorking as any)?.presentedForm;
    const list = (Array.isArray(pf) ? pf : (pf ? [pf] : [])) as any[];
    const next = list
      .map((x) => this.normalizeUploadUiFileToAttachment(x))
      .filter(Boolean)
      .filter((a: any) => ((a as any)?.url ?? '').toString().trim() !== (url ?? '').toString().trim()) as Attachment[];
    (this.diagnosticReportWorking as any).presentedForm = next;
  }

  removeAttachmentAtIndex(index: number): void {
    const pf = (this.diagnosticReportWorking as any)?.presentedForm;
    const list = (Array.isArray(pf) ? pf : (pf ? [pf] : [])) as any[];
    const next = list
      .map((x) => this.normalizeUploadUiFileToAttachment(x))
      .filter(Boolean) as Attachment[];
    if (index < 0 || index >= next.length) return;
    next.splice(index, 1);
    (this.diagnosticReportWorking as any).presentedForm = next;
  }

  puttedObservationsV2: Observation[] = [];
  puttedImagingStudiesV2: ImagingStudy[] = [];
  toReferenceResultsV2: 'Observation' | 'ImagingStudy' | null = null;
  @Input() serviceRequestIdsV2?: string[];
  isSuperReportV2: boolean = false;
  isEditableV2 = true;

  @Input() serviceRequestIdV2!: string;

  data = inject(MAT_DIALOG_DATA, { optional: true }) as {
    serviceRequestId?: string;
    serviceRequestIds?: string[];

  }

  //  category: this.baseFunctionToRetrieveValueset,
  //     code: this.baseFunctionToRetrieveValueset,
  //     performer: this.baseFunctionToTurnPractitionerIntoReference,
  //     resultInterpreter:

  ngOnInit() {
    console.log("service request id v2:", this.data);

    // Prefer using passed ServiceRequest objects (dialog/input) so callers can provide any number of SRs.
    const contextSrs = this.getContextServiceRequests();
    const contextPrimary = contextSrs[0] ?? this.getContextServiceRequest();
    this.resolvedPatientIdV2 = this.resolvePatientIdFromContext(contextPrimary);

    // Merge ids from all possible sources (inputs + MAT_DIALOG_DATA + SR objects)
    const idsFromContext = (contextSrs || []).map((sr: any) => (sr?.id ?? '').toString()).filter(Boolean);
    const idsFromDialogData = ((this.dialogData as any)?.serviceRequestIds ?? []) as any[];
    const idsFromMatData = ((this.data as any)?.serviceRequestIds ?? []) as any[];

    this.serviceRequestIdsV2 = Array.from(new Set([
      ...((this.serviceRequestIdsV2 ?? []) as any[]),
      ...idsFromContext,
      ...idsFromDialogData,
      ...idsFromMatData,
    ].map((x) => (x ?? '').toString()).filter((x) => !!x && x !== 'undefined' && x !== 'null')));

    // Establish a primary SR id (used for the initial DR lookup); do not force 'undefined'
    const primaryId =
      (this.serviceRequestIdV2 ?? '').toString().trim() ||
      ((this.data as any)?.serviceRequestId ?? '').toString().trim() ||
      ((this.dialogData as any)?.serviceRequestId ?? '').toString().trim() ||
      ((contextPrimary as any)?.id ?? '').toString().trim() ||
      (this.serviceRequestIdsV2?.[0] ?? '').toString().trim();

    this.serviceRequestIdV2 = primaryId;

    console.log('service request id v2 after:', this.serviceRequestIdV2);

    // Decide init flow:
    // - Single SR: initFromPrimaryServiceRequest (also sets existingReport + UI mode)
    // - Grouped SR ids: loadContextAndReferences (strict basedOn match for all SR ids)
    const initialSrIds = this.collectServiceRequestIdsForBasedOn(contextSrs);
    this.serviceRequestIdsV2 = initialSrIds;
    this.isSuperReportV2 = (initialSrIds?.length ?? 0) > 1;

    const startWithPrimary$ = (contextPrimary && (contextPrimary as any)?.id && (contextPrimary as any).id.toString() === primaryId)
      ? of(contextPrimary as ServiceRequest)
      : (primaryId
        ? this.http.get<ServiceRequest>(`${this.backendApiBaseUrl}/ServiceRequest/${primaryId}`).pipe(
          catchError((err) => {
            console.log(err);
            this.errorService.openandCloseError('Failed to load ServiceRequest for this report.');
            return of(null as any);
          })
        )
        : of(null as any));

    startWithPrimary$.pipe(take(1)).subscribe((sr: ServiceRequest | null) => {
      if (!sr) return;
      this.serviceRequest = sr;

      if (this.isSuperReportV2) {
        // Group init uses strict basedOn set equality across all SR ids.
        this.loadContextAndReferences(sr);
      } else {
        this.initFromPrimaryServiceRequest(sr);
      }
    });

    // see if there is a ready report -> iseditable beomes constructed depending n the statis

    //its category > wheher obs or Imaging or nothing
    //getting associated obs -> iif there are


    forkJoin({
      category: this.formFieldService.getFormFieldSelectData('diagnosticReport', 'category'),
      code: this.formFieldService.getFormFieldSelectData('diagnosticReport', 'code'),
      performer: this.formFieldService.getFormFieldSelectData('diagnosticReport', 'performer'),
      resultsInterpreter: this.formFieldService.getFormFieldSelectData('diagnosticReport', "resultsInterpreter"),
    }).subscribe({
      next: (g: any) => {
        this.labReportForm = new Map([


          ['status', {
            formFields: [
              <SingleCodeField>{
                generalProperties: <generalFieldsData>{
                  auth: {
                    read: 'all',
                    write: 'doctor, nurse'
                  },
                  fieldApiName: 'status',
                  fieldName: "Status",
                  fieldType: 'SingleCodeField',
                  inputType: 'text',
                  isArray: false,
                  isGroup: false,

                },
                data: 'registered | partial | preliminary | modified | final | amended | corrected | appended | cancelled | entered-in-error | unknown'.split(" | "),
              },
            ]
          }],





          [
            'category', {
              formFields: [
                <CodeableConceptField>{
                  generalProperties: <generalFieldsData>{
                    auth: {
                      read: 'all',
                      write: 'doctor, nurse'
                    },
                    fieldApiName: 'category',
                    fieldName: "Lab Report Category",
                    fieldType: 'CodeableConceptField',
                    fieldHint: "Choose from suggested category or enter a new one",
                    inputType: 'text',
                    isArray: false,
                    isGroup: false,

                  },
                  data: g.category,
                },
              ]
            }
          ],





          [
            'code',
            {
              formFields: [
                <CodeableConceptFieldFromBackEnd>{
                  generalProperties: <generalFieldsData>{
                    auth: {
                      read: 'all',
                      write: 'doctor, nurse'
                    },
                    fieldApiName: 'code',
                    fieldName: "Lab Report Name",
                    fieldType: 'CodeableConceptFieldFromBackEnd',
                    fieldHint: "Choose from suggested names or enter your own",
                    inputType: 'text',
                    isArray: false,
                    isGroup: false,
                    validations: [
                      {

                        name: "required",
                        type: "default",
                      }
                    ]

                  },
                  data: g.code,
                },
              ]
            }
          ],





          [
            'performer', {
              formFields: [
                <IndividualReferenceField>{
                  generalProperties: <generalFieldsData>{
                    auth: {
                      read: 'all',
                      write: 'doctor, nurse'
                    },
                    fieldApiName: 'performer',
                    fieldName: "Performer",
                    fieldHint: "Who prepared this report",
                    fieldType: 'IndividualReferenceField',
                    inputType: 'text',
                    isArray: false,
                    isGroup: false,
                    isHidden: true,

                  },
                  data: g.performer,
                },
              ]
            }
          ],





          [
            'resultsInterpreter', {
              formFields: [
                <IndividualReferenceField>{
                  generalProperties: <generalFieldsData>{
                    auth: {
                      read: 'all',
                      write: 'doctor, nurse'
                    },
                    fieldApiName: 'resultsInterpreter',
                    fieldName: "Result Interpreter",
                    fieldType: 'IndividualReferenceField',
                    inputType: 'text',
                    isArray: false,
                    isGroup: false,
                    isHidden: true,

                  },
                  data: g.resultsInterpreter,
                },
              ]
            }
          ],





          ['conclusion', {
            formFields: [
              <IndividualField>{
                generalProperties: <generalFieldsData>{
                  auth: {
                    read: 'all',
                    write: 'doctor, nurse'
                  },
                  fieldApiName: 'conclusion',
                  fieldName: "Conclusion",
                  fieldHint: "Conclusion of the report (e.g. free text summary)",

                  fieldType: 'IndividualField',
                  inputType: 'textarea',
                  isArray: false,
                  isGroup: false,
                  validations: [
                    {

                      name: "required",
                      type: "default",
                    }
                  ]

                },
                data: '',
              },
            ]
          }],





          ['presentedForm', {
            formFields: [
              <IndividualField>{
                generalProperties: <generalFieldsData>{
                  auth: {
                    read: 'all',
                    write: 'doctor, nurse'
                  },
                  fieldApiName: 'attachment',
                  fieldName: "Attach Report Documents and Files",
                  fieldHint: "Upload relevant documents or files for the report",
                  fieldType: 'IndividualField',
                  inputType: 'photo_upload',
                  isArray: false,
                  isGroup: false,

                },
                data: '',
              },
            ]
          }],


        ])
        this.toPassLabForm = this.utilityService.convertFormFields(this.labReportForm);

        // Note: initialization of existing report / references is done earlier in ngOnInit.

      },
      error: (e: any) => {
        console.log(e);
        this.errorService.openandCloseError("An error ocurred while fetching data t be used in drop downs");
      }
    })
  }
  dialog = inject(MatDialog)

  private getContextServiceRequest(): ServiceRequest | null {
    return (this.serviceRequest ?? this.dialogData?.serviceRequest ?? null) as ServiceRequest | null;
  }

  private getContextServiceRequests(): ServiceRequest[] {
    const fromInputs = (this.serviceRequests ?? []) as ServiceRequest[];
    const fromDialog = (this.dialogData?.serviceRequests ?? []) as ServiceRequest[];
    const single = this.getContextServiceRequest();

    const combined = [...fromInputs, ...fromDialog, ...(single ? [single] : [])]
      .filter(Boolean)
      // de-dupe by id when available
      .filter((sr, idx, arr) => {
        const id = (sr?.id ?? '').toString();
        if (!id) return idx === arr.indexOf(sr);
        return arr.findIndex((x) => (x?.id ?? '').toString() === id) === idx;
      });

    return combined;
  }

  private resolvePatientIdFromContext(sr: ServiceRequest | null): string | null {
    const fromInput = (this.patientId ?? this.dialogData?.patientId ?? null) as string | null;
    if (fromInput) return fromInput;
    const ref = (sr as any)?.subject?.reference as string | undefined;
    if (ref && ref.startsWith('Patient/')) return ref.split('/')[1] || null;
    return this.utilityService.getPatientIdFromRoute() || null;
  }

  private collectServiceRequestIdsForBasedOn(serviceRequests: ServiceRequest[]): string[] {
    const normalize = (v: any): string | null => {
      const s = (v ?? '').toString().trim();
      if (!s) return null;
      const lower = s.toLowerCase();
      if (lower === 'undefined' || lower === 'null') return null;
      return s;
    };

    const ids = new Set<string>();

    for (const sr of (serviceRequests ?? [])) {
      const id = normalize((sr as any)?.id);
      if (id) ids.add(id);
    }

    const v2Primary = normalize(this.serviceRequestIdV2);
    if (v2Primary) ids.add(v2Primary);

    for (const id of (this.serviceRequestIdsV2 ?? [])) {
      const n = normalize(id);
      if (n) ids.add(n);
    }

    const fromMatData = this.data as any;
    const dataId = normalize(fromMatData?.serviceRequestId);
    if (dataId) ids.add(dataId);
    for (const id of (fromMatData?.serviceRequestIds ?? [])) {
      const n = normalize(id);
      if (n) ids.add(n);
    }

    const fromDialogData = this.dialogData as any;
    const dlgId = normalize(fromDialogData?.serviceRequestId);
    if (dlgId) ids.add(dlgId);
    for (const id of (fromDialogData?.serviceRequestIds ?? [])) {
      const n = normalize(id);
      if (n) ids.add(n);
    }

    return Array.from(ids);
  }

  private ensureServiceRequestsLoaded$(existing: ServiceRequest[], ids: string[]): Observable<ServiceRequest[]> {
    const list = (existing ?? []).filter(Boolean) as ServiceRequest[];
    const idSet = new Set((list || []).map((sr: any) => (sr?.id ?? '').toString()).filter(Boolean));
    const missing = (ids ?? []).filter((id) => !!id && !idSet.has(id));
    if (!missing.length) return of(list);

    return forkJoin(
      missing.map((id) =>
        this.http.get<ServiceRequest>(`${this.backendApiBaseUrl}/ServiceRequest/${id}`).pipe(
          catchError(() => of(null as any))
        )
      )
    ).pipe(
      map((fetched) => [...list, ...(fetched || []).filter(Boolean)] as ServiceRequest[])
    );
  }

  private loadContextAndReferences(primarySr: ServiceRequest) {
    const srsExisting = this.getContextServiceRequests();
    const srIds = this.collectServiceRequestIdsForBasedOn(srsExisting.length ? srsExisting : [primarySr]);
    this.serviceRequestIdsV2 = srIds;

    const resolveAllSrs$ = this.ensureServiceRequestsLoaded$(srsExisting.length ? srsExisting : [primarySr], srIds);

    resolveAllSrs$.pipe(take(1)).subscribe((resolvedSrs) => {
      const primary = resolvedSrs[0] ?? primarySr;
      this.serviceRequests = resolvedSrs;

      // Category decides whether we treat results as Observations vs ImagingStudy
      const cat = primary.category?.[0]?.coding?.[0]?.code ?? primary.category?.[0]?.text ?? 'unknown';
      const catText = (cat ?? '').toString().toLowerCase();
      if (catText.includes('laboratory') || catText.includes('vital')) {
        this.toReferenceResultsV2 = 'Observation';
      } else if (catText.includes('imaging')) {
        this.toReferenceResultsV2 = 'ImagingStudy';
      }

      // Items Required should be available for grouped reports too.
      this.initItemsRequiredStep(primary);

      // --- Find an existing DiagnosticReport for this exact SR group.
      const primaryId = (primary as any)?.id?.toString?.() ?? (this.serviceRequestIdV2 ?? '').toString().trim();
      if (!primaryId) {
        this.seedNewReportFromPrimary(primary);
        return;
      }

      const subjectRef = (primary as any)?.subject?.reference ?? '';
      const strict = true; // group view requires basedOn set equality

      this.utilityService.threeTierQueryAndSyncToStateOnce$<DiagnosticReport>({
        resourceType: 'DiagnosticReport',
        urlSearchObject: {
          ...(!this.backendApiBaseUrl.includes('server.fire') && subjectRef ? { subject: subjectRef } : {}),
          'based-on': `ServiceRequest/${primaryId}`,
        },
        querySearchObject: [
          ...(subjectRef ? [{ key: 'subject.reference', value: `${subjectRef}` }] : []),
          {
            key: 'basedOn.reference',
            value: `${subjectRef?.includes('https') || subjectRef?.includes('http')
              ? this.backendApiBaseUrl + '/ServiceRequest/' + primaryId
              : 'ServiceRequest/' + primaryId}`
          }
        ]
      }).pipe(take(1)).subscribe((list) => {
        const chosen = this.pickPreferredDiagnosticReport(list ?? [], srIds, strict);

        if (!chosen) {
          this.isEditableV2 = true;
          this.seedNewReportFromPrimary(primary);
          return;
        }

        this.applyExistingDiagnosticReportToState(primary, chosen as any);
      });
    });
  }

  onObservationsSelectionChanged(observations: Observation[]): void {
    const list = (observations ?? []).filter(Boolean) as any[];
    this.puttedObservationsV2 = list as Observation[];

    const nextRefs: Reference[] = [];
    for (const o of list) {
      const id = (o?.id ?? '').toString();
      if (!id) continue;
      nextRefs.push({
        reference: `Observation/${id}`,
        display: o?.code?.text ?? o?.code?.coding?.[0]?.display ?? undefined
      } as any);
    }

    const byRef = new Map<string, Reference>();
    for (const r of nextRefs) {
      const k = (r?.reference ?? '').toString();
      if (k && !byRef.has(k)) byRef.set(k, r);
    }
    this.selectedObservationRefs = Array.from(byRef.values());
  }

  onImagingStudiesSelectionChanged(studies: ImagingStudy[]): void {
    const list = (studies ?? []).filter(Boolean) as any[];
    this.puttedImagingStudiesV2 = list as ImagingStudy[];

    const nextRefs: Reference[] = [];
    for (const s of list) {
      const id = (s?.id ?? '').toString();
      if (!id) continue;
      nextRefs.push({
        reference: `ImagingStudy/${id}`,
        display: (s as any)?.description ?? (s as any)?.series?.[0]?.description ?? undefined
      } as any);
    }

    const byRef = new Map<string, Reference>();
    for (const r of nextRefs) {
      const k = (r?.reference ?? '').toString();
      if (k && !byRef.has(k)) byRef.set(k, r);
    }
    this.selectedImagingStudyRefs = Array.from(byRef.values());
  }

  onDiagnosticReportFormChanged(values: any): void {
    this.diagnosticReportFormValues = values;
  }

  switchDiagnosticReportToReview(): void {
    if (!this.isEditableV2) {
      this.diagnosticReportUiMode = 'review';
      return;
    }

    const values = this.formCmp?.aForm?.value ?? this.diagnosticReportFormValues;
    if (!values) {
      this.errorService.openandCloseError('Form is not ready yet.');
      return;
    }

    this.diagnosticReportWorking = this.applySubmittedDataToDiagnosticReport(values, this.diagnosticReportWorking);
    this.diagnosticReportUiMode = 'review';
  }

  switchDiagnosticReportToForm(): void {
    if (!this.isEditableV2) return;
    this.diagnosticReportUiMode = 'form';

    // Patch current working values back into the form so switching tabs doesn't lose state.
    const submitted = this.mapDiagnosticReportToSubmittedData(this.diagnosticReportWorking);
    queueMicrotask(() => {
      try {
        this.formCmp?.aForm?.patchValue(submitted ?? {}, { emitEvent: false });
      } catch {
        // best-effort; form might not be initialized yet
      }
    });
  }

  // Map DiagnosticReport -> submitted data shape expected by ResourceDataReview/DynamicForms
  readonly mapDiagnosticReportToSubmittedData = (resource: any): any => {
    const dr: any = resource ?? {};

    const toFlatConcept = (cc: any): any => {
      if (!cc) return null;
      // Already in the expected "flat" shape
      if (typeof cc === 'object' && (cc.code || cc.display) && !cc.coding) {
        return {
          code: cc.code,
          display: cc.display ?? cc.text ?? cc.code,
          system: cc.system,
          text: cc.text ?? cc.display ?? cc.code
        };
      }
      const coding = (cc?.coding ?? [])[0];
      if (coding?.code || coding?.display) {
        return {
          code: coding.code,
          display: coding.display ?? coding.code,
          system: coding.system,
          text: cc?.text ?? coding.display ?? coding.code
        };
      }
      if (cc?.text) return { text: cc.text };
      return cc;
    };

    return {
      status: (dr?.status ?? 'registered'),
      category: toFlatConcept(((dr?.category ?? [])[0] ?? null)),
      code: toFlatConcept((dr?.code ?? null)),
      performer: (dr?.performer ?? [])[0] ?? null,
      resultsInterpreter: (dr?.resultsInterpreter ?? [])[0] ?? null,
      conclusion: dr?.conclusion ?? '',
      // Our form uses apiName 'attachment' for presentedForm
      attachment: dr?.presentedForm ?? []
    };
  };

  // Map submitted data -> DiagnosticReport (FHIR-shaped)
  readonly applySubmittedDataToDiagnosticReport = (submitted: any, resource: any): any => {
    const base: any = structuredClone(resource ?? { resourceType: 'DiagnosticReport' });
    const values = submitted ?? {};
    const transformed = this.fhirTransformService.transformValues('DiagnosticReport', values, ['attachment']) as any;

    const next: any = {
      ...base,
      ...transformed,
      resourceType: 'DiagnosticReport'
    };

    // Map attachment -> presentedForm
    if (values.hasOwnProperty('attachment')) {
      const pf = values.attachment;
      next.presentedForm = Array.isArray(pf) ? pf : (pf ? [pf] : []);
    }

    // Ensure arrays for fields that are arrays in FHIR
    if (next.category && !Array.isArray(next.category)) next.category = [next.category];
    if (next.performer && !Array.isArray(next.performer)) next.performer = [next.performer];
    if (next.resultsInterpreter && !Array.isArray(next.resultsInterpreter)) next.resultsInterpreter = [next.resultsInterpreter];

    return next;
  };

  onDiagnosticReportWorkingUpdated(next: any): void {
    if (!next) return;
    this.diagnosticReportWorking = next;
  }

  onSpecimensSelectionChanged(specimens: Specimen[]): void {
    const list = (specimens ?? []).filter(Boolean);
    this.selectedSpecimensV2 = list;

    // DiagnosticReport.specimen expects Reference objects.
    const nextRefs: Reference[] = [];
    for (const s of list as any[]) {
      const id = (s?.id ?? '').toString();
      if (!id) continue;
      nextRefs.push({
        reference: `Specimen/${id}`,
        display: s?.type?.text ?? s?.type?.coding?.[0]?.display ?? undefined
      } as any);
    }
    // de-dupe by reference
    const byRef = new Map<string, Reference>();
    for (const r of nextRefs) {
      const k = (r?.reference ?? '').toString();
      if (k && !byRef.has(k)) byRef.set(k, r);
    }
    this.selectedSpecimenRefs = Array.from(byRef.values());
  }

  private fetchAvailableObservations(srRefs: string[]): Observable<Observation[]> {
    if (srRefs?.length) {
      return forkJoin(
        srRefs.map((srRef) =>
          this.http
            .get<Bundle<Observation>>(`${this.backendApiBaseUrl}/Observation?based-on=${encodeURIComponent(srRef)}&status=final&_count=200`)
            .pipe(
              map((b: any) => (b?.entry || []).map((e: any) => e?.resource).filter(Boolean) as Observation[]),
              map((list: Observation[]) => (list || []).filter((o: any) => {
                const st = (o?.status ?? '').toString().toLowerCase();
                const basedOn = (o?.basedOn ?? []) as any[];
                const linked = basedOn.some((b: any) => (b?.reference ?? '').toString() === srRef);
                return st === 'final' && linked;
              })),
              catchError(() => of([] as Observation[]))
            )
        )
      ).pipe(
        map((lists: Observation[][]) => {
          const flat = (lists || []).flat();
          const byId = new Map<string, Observation>();
          for (const o of flat) {
            const id = (o as any)?.id?.toString() ?? '';
            const key = id ? `Observation/${id}` : JSON.stringify(o);
            if (!byId.has(key)) byId.set(key, o);
          }
          return Array.from(byId.values());
        })
      );
    }

    return this.http
      .get<Bundle<Observation>>(`${this.backendApiBaseUrl}/Observation?_count=200`)
      .pipe(
        map((b: any) => (b?.entry || []).map((e: any) => e?.resource).filter(Boolean) as Observation[]),
        catchError(() => of([] as Observation[]))
      );
  }

  private fetchAvailableSpecimens(srRefs: string[]): Observable<Specimen[]> {
    if (srRefs?.length) {
      return forkJoin(
        srRefs.map((srRef) => {

          return this.http
            .get<Bundle<Specimen>>(`${this.backendApiBaseUrl}/Specimen?request=${encodeURIComponent(srRef)}&_count=200`)
            .pipe(
              map((b: any) => (b?.entry || []).map((e: any) => e?.resource).filter(Boolean) as Specimen[]),
              map((list: Specimen[]) => (list || []).filter((s: any) => {
                const req = (s?.request ?? []) as any[];
                return req.some((r: any) => (r?.reference ?? '').toString() === srRef);
              })),
              catchError(() => of([] as Specimen[]))
            )
        }
        )
      ).pipe(
        map((lists: Specimen[][]) => {
          const flat = (lists || []).flat();
          const byId = new Map<string, Specimen>();
          for (const s of flat) {
            const id = (s as any)?.id?.toString() ?? '';
            const key = id ? `Specimen/${id}` : JSON.stringify(s);
            if (!byId.has(key)) byId.set(key, s);
          }
          return Array.from(byId.values());
        })
      );
    }

    return this.http
      .get<Bundle<Specimen>>(`${this.backendApiBaseUrl}/Specimen?_count=200`)
      .pipe(
        map((b: any) => (b?.entry || []).map((e: any) => e?.resource).filter(Boolean) as Specimen[]),
        catchError(() => of([] as Specimen[]))
      );
  }

  addObservationReference() {
    if (!this.availableObservations.length) {
      this.infoDialog.show('No Observation is available to select.', { title: 'No Observation', duration: 3500 });
      return;
    }

    const rawTableData = {
      entry: (this.availableObservations || []).map((resource: any) => ({ resource }))
    };

    const ref = this.dialog.open(TabledOptionComponent, {
      maxWidth: '900px',
      maxHeight: '93vh',
      data: {

        headerFilter: new Map<string, string[]>([

          ['status', ['final', 'registered', 'preliminary', 'partial']],
          ['category', ['vital-signs', 'imaging', 'laboratory', 'procedure']],
        ]),
        columnMetaData: new Map([['code', {
          dataType: "CodeableConceptField",
          columnName: "Name"
        }],
        ['issued', {
          inputType: "datetime-local",

          columnName: "Date Issued"
        }],
        ['status', {
          dataType: "SingleCodeField",
          displayStyle: "chips",
          columnName: "Status"
        }],
        ['category', {
          dataType: "CodeableConceptField",

          columnName: "Category"
        }],
        ['valueString', {
          dataType: "IndividualField",
          displayStyle: "normal",
          columnName: "Value"
        }]


        ]) as Map<string, {
          dataType?: fieldType,
          displayStyle?: 'normal' | 'chips' | any,
          inputType?: any,
          columnName?: string
        }>,

        rawTableData,
        columns: ['issued', 'code', 'category', 'status', 'valueString'],
        isToBeSelected: true,
      }

    })

    ref.afterClosed().subscribe((result: any) => {
      const picked = result?.reference as Reference | undefined;
      if (!picked) return;
      const pickedRef = (picked.reference ?? '').toString().trim();
      if (!pickedRef) return;
      if (this.selectedObservationRefs.some((r) => (r?.reference ?? '') === pickedRef)) return;
      this.selectedObservationRefs = [...this.selectedObservationRefs, picked];
    });
  }

  addSpecimenReference() {
    if (!this.availableSpecimens.length) {
      this.infoDialog.show('No Specimen is available to select.', { title: 'No Specimen', duration: 3500 });
      return;
    }

    const rawTableData = {
      entry: (this.availableSpecimens || []).map((resource: any) => ({ resource }))
    };

    const ref = this.dialog.open(TabledOptionComponent, {
      maxWidth: '900px',
      maxHeight: '93vh',
      data: {
        headerFilter: new Map<string, string[]>([]),
        columnMetaData: new Map([
          ['type', { dataType: 'CodeableConceptField', columnName: 'Type' }],
          ['status', { dataType: 'SingleCodeField', displayStyle: 'chips', columnName: 'Status' }],
          ['receivedTime', { inputType: 'datetime-local', columnName: 'Received' }],
        ]) as Map<string, { dataType?: fieldType; displayStyle?: 'normal' | 'chips' | any; inputType?: any; columnName?: string }>,
        rawTableData,
        columns: ['receivedTime', 'type', 'status'],
        isToBeSelected: true,
      }
    });

    ref.afterClosed().subscribe((result: any) => {
      const picked = result?.reference as Reference | undefined;
      if (!picked) return;
      const pickedRef = (picked.reference ?? '').toString().trim();
      if (!pickedRef) return;
      if (this.selectedSpecimenRefs.some((r) => (r?.reference ?? '') === pickedRef)) return;
      this.selectedSpecimenRefs = [...this.selectedSpecimenRefs, picked];
    });
  }

  removeObservationReference(index: number) {
    this.selectedObservationRefs = this.selectedObservationRefs.filter((_, i) => i !== index);
  }

  removeSpecimenReference(index: number) {
    this.selectedSpecimenRefs = this.selectedSpecimenRefs.filter((_, i) => i !== index);
  }

  private initItemsRequiredStep(serviceRequest: ServiceRequest): void {
    this.itemsRequiredLoading = true;
    this.itemsRequiredSource = 'none';
    this.resolvedActivityDefinition = null;
    this.itemsRequiredDirty = false;
    this.itemsRequiredEditEnabled = true;
    this.itemsRequiredReadOnlyNote = null;

    const primaryId = (serviceRequest as any)?.id?.toString?.() ?? '';
    const groupIds = Array.from(new Set([primaryId, ...(this.serviceRequestIdsV2 || [])].filter(Boolean)));

    const resolveGroupServiceRequests$ = groupIds.length > 1
      ? forkJoin(groupIds.map((id) => this.http.get<ServiceRequest>(`${this.backendApiBaseUrl}/ServiceRequest/${id}`).pipe(catchError(() => of(null as any)))))
        .pipe(map((list) => (list || []).filter(Boolean) as ServiceRequest[]))
      : of([serviceRequest]);

    forkJoin({
      itemsRequiredOptions: this.formFieldService.getFormFieldSelectData('activityDefinition', 'itemsRequired').pipe(
        catchError(() => of([] as any[]))
      ),
      serviceRequests: resolveGroupServiceRequests$
    }).pipe(
      switchMap(({ itemsRequiredOptions, serviceRequests }) => {
        const srs = (serviceRequests || []).length ? serviceRequests : [serviceRequest];
        const distinctCodes = Array.from(new Set(srs.map((sr) => this.getServiceRequestCode(sr)).filter(Boolean) as string[]));

        // Build the form once; we can render read-only by disabling edit buttons.
        this.itemsRequiredForm = [
          {
            generalProperties: {
              isArray: true,
              isGroup: true,
              fieldApiName: 'itemsRequired',
              fieldName: 'Items Required',
              fieldLabel: 'Items Required',
              groupFieldsHint: 'Edit the materials/items required for this service request. Quantity unit is preserved from the default configuration.',
              fieldType: 'IndividualField',
            },
            keys: ['items', 'quantity'],
            groupFields: {
              items: {
                generalProperties: {
                  isArray: false,
                  isGroup: false,
                  fieldApiName: 'items',
                  fieldName: 'Item',
                  fieldLabel: 'Item',
                  fieldType: 'CodeableConceptField',
                },
                data: itemsRequiredOptions ?? [],
              },
              quantity: {
                generalProperties: {
                  isArray: false,
                  isGroup: false,
                  fieldApiName: 'quantity',
                  fieldName: 'Quantity',
                  fieldLabel: 'Quantity',
                  fieldType: 'IndividualField',
                  inputType: 'number',
                },
              },
            },
          } as any
        ] as unknown as FormFields[];

        // Mixed-code group: show combined list and disable edits.
        if (distinctCodes.length > 1) {
          this.itemsRequiredEditEnabled = false;
          this.itemsRequiredReadOnlyNote = 'This report contains multiple ServiceRequests with different codes. Items Required is shown as a combined list (quantities are summed when possible). Editing is disabled here; update defaults in Service Details.';

          return forkJoin(
            srs.map((sr) => this.resolveActivityDefinitionForServiceRequest(sr).pipe(catchError(() => of(null))))
          ).pipe(
            map((defs) => ({ mode: 'mixed' as const, defs, srs }))
          );
        }

        // Single-code (or single SR): resolve one ActivityDefinition (instance override or default)
        return this.resolveActivityDefinitionForServiceRequest(serviceRequest).pipe(
          catchError(() => of(null)),
          map((def) => ({ mode: 'single' as const, def }))
        );
      })
    ).subscribe({
      next: (result: any) => {
        if (result?.mode === 'mixed') {
          const defs = Array.isArray(result?.defs) ? result.defs : [];
          const allRows = defs.flatMap((d: any) => extractItemsRequiredRows(d));
          const combined = this.combineItemsRequiredRows(allRows);
          this.resolvedActivityDefinition = null;
          this.itemsRequiredSource = 'none';
          this.originalItemsRequiredRows = structuredClone(combined ?? []);
          this.itemsRequiredRows = structuredClone(combined ?? []);

          this.itemsRequiredDirty = false;
          this.itemsRequiredLoading = false;
          return;
        }

        const activityDefinition = result?.def ?? null;
        this.resolvedActivityDefinition = activityDefinition;

        const extracted = extractItemsRequiredRows(activityDefinition);
        this.originalItemsRequiredRows = structuredClone(extracted ?? []);
        this.itemsRequiredRows = structuredClone(extracted ?? []);
        console.log('Extracted itemsRequired rows from resolved ActivityDefinition', this.itemsRequiredRows);
        this.itemsRequiredDirty = false;
        this.itemsRequiredLoading = false;
      },
      error: (err) => {
        console.error(err);
        this.itemsRequiredLoading = false;
      }
    });
  }
  changeItemsRequiredRowsToResourceDataFormat(next: ItemsRequiredRow[]): any[] {
    return (next ?? []).map((r) => ({
      items: r.items.coding ? r.items.coding[0] : r.items,
      quantity: r.quantity
    }));
  }

  private combineItemsRequiredRows(rows: ItemsRequiredRow[]): ItemsRequiredRow[] {
    const keyForItem = (concept: any): string => {
      const coding = concept?.coding?.[0] ?? concept;
      const system = (coding?.system ?? '').toString().trim().toLowerCase();
      const code = (coding?.code ?? '').toString().trim().toLowerCase();
      if (system && code) return `${system}::${code}`;

      const text = (concept?.text ?? concept?.display ?? concept?.coding?.[0]?.display ?? '').toString().trim().toLowerCase();
      return text;
    };

    const byKey = new Map<string, { item: any; quantity: number | null }>();

    for (const row of rows || []) {
      const item = (row as any)?.items;
      if (!item) continue;
      const key = keyForItem(item);
      if (!key) continue;

      const qRaw = (row as any)?.quantity;
      const q = qRaw === '' || qRaw === null || qRaw === undefined ? null : Number(qRaw);
      const qNum = Number.isFinite(q as any) ? (q as number) : null;

      if (!byKey.has(key)) {
        byKey.set(key, { item, quantity: qNum });
      } else {
        const prev = byKey.get(key)!;
        if (prev.quantity === null) {
          prev.quantity = qNum;
        } else if (qNum !== null) {
          prev.quantity = prev.quantity + qNum;
        }
      }
    }

    return Array.from(byKey.values()).map((v) => ({ items: v.item, quantity: v.quantity }));
  }

  private resolveActivityDefinitionForServiceRequest(serviceRequest: ServiceRequest): Observable<any | null> {
    const inst = (serviceRequest as any)?.instantiatesCanonical;
    const canonical = Array.isArray(inst) ? inst[0] : inst;
    const instanceId = this.parseIdFromCanonical(canonical, 'ActivityDefinition');

    // Instance override: fetch by id (works even if status is retired)
    if (instanceId) {
      this.itemsRequiredSource = 'instance';
      return this.http.get<any>(`${this.backendApiBaseUrl}/ActivityDefinition/${instanceId}`).pipe(
        catchError(() => of(null))
      );
    }

    // Default: choose ACTIVE ActivityDefinition matching the ServiceRequest code
    const code = this.getServiceRequestCode(serviceRequest);
    if (!code) {
      this.itemsRequiredSource = 'none';
      return of(null);
    }

    this.itemsRequiredSource = 'default';
    return this.ensureActiveActivityDefinitionsLoaded$().pipe(
      map((defs) => {
        console.log(defs);
// $#$        // /* debugger; */
        const matches = (defs || []).filter((d: any) => (d?.code as any)?.coding?.[0]?.code === code);
        const withTopic = matches.find((d: any) => (d?.topic || []).some((t: any) => ((t?.text ?? '').toString().toLowerCase()).includes('items usage computation')));
        const toReturn = withTopic ?? matches[0] ?? null;
        console.log('Resolved ActivityDefinition for SR code', code, toReturn);
        return toReturn;
      })
    );
  }

  // private ensureActiveActivityDefinitionsLoaded$(): Observable<any[]> {
  //   return this.stateService.orgWideResources.activityDefinitions.pipe(
  //     take(1),
  //     switchMap((wrapped: any[]) => {
  //       const existing = (wrapped || []).map((w) => w?.actualResource).filter(Boolean);
  //       if (existing.length) return of(existing);

  //       return this.http.get<any>(`${this.backendApiBaseUrl}/ActivityDefinition?status=active&_count=500`).pipe(
  //         map((bundle: any) => (bundle?.entry || []).map((e: any) => e?.resource).filter(Boolean)),
  //         tap((resources: any[]) => {
  //           this.stateService.orgWideResources.activityDefinitions.next(
  //             (resources || []).map((d: any) => ({
  //               actualResource: d,
  //               referenceId: d?.id ? `ActivityDefinition/${d.id}` : null,
  //               savedStatus: 'saved'
  //             }))
  //           );
  //         }),
  //         catchError(() => of([] as any[]))
  //       );
  //     })
  //   );
  // }
  private ensureActiveActivityDefinitionsLoaded$(): Observable<any[]> {
    // return this.stateService.orgWideResources.activityDefinitions.pipe(
    //   take(1),
    //   switchMap((wrapped: any[]) => {
    // const existing = (wrapped || []).map((w) => w?.actualResource).filter(Boolean);
    // if (existing.length) return of(existing);

    // Use three-tier query (DTR pattern). Do NOT include code in urlSearchObject.
    return this.utilityService.threeTierQueryAndSyncToStateOnce$<any>({
      resourceType: 'ActivityDefinition',
      urlSearchObject: {
        status: 'active',
      },
      querySearchObject: [{ key: 'status', value: 'active' }],
      scope: 'orgWide',
      // count: 2000,
      expectedMinLength: 200,
      tier1FollowNextLinks: true,
      tier1MaxPages: 10,
    }).pipe(
      // tap((resources: any[]) => {
      //   // Keep existing behavior: hydrate orgWideResources.activityDefinitions if it was empty.
      //   this.stateService.orgWideResources.activityDefinitions.next(
      //     (resources || []).map((d: any) => ({
      //       actualResource: d,
      //       referenceId: d?.id ? `ActivityDefinition/${d.id}` : null,
      //       savedStatus: 'saved',
      //     }))
      //   );
      // }),
      catchError(() => of([] as any[]))
    );
    //   })
    // );
  }


  private getServiceRequestCode(sr: ServiceRequest): string | null {
    const code = (sr as any)?.code?.coding?.[0]?.code ?? (sr as any)?.code?.text ?? null;
    return code ? code.toString().trim() : null;
  }

  private parseIdFromCanonical(canonical: any, expectedType: string): string | null {
    if (!canonical || typeof canonical !== 'string') return null;
    const raw = canonical.toString().trim();
    if (!raw) return null;

    const noFrag = raw.split('#')[0].split('?')[0];
    const parts = noFrag.split('/').filter(Boolean);
    if (parts.length < 2) return null;

    const id = parts[parts.length - 1];
    const type = parts[parts.length - 2];
    if (type !== expectedType) return null;
    return id || null;
  }

  private activityDefinitionToCanonical(def: any): string | null {
    const id = (def?.id ?? '').toString().trim();
    if (!id) return null;
    const base = this.backendApiBaseUrl.replace(/\/+$/, '');
    return `${base}/ActivityDefinition/${id}`;
  }

  private getServiceRequestIdsMissingInstantiatesCanonical(serviceRequests: ServiceRequest[]): string[] {
    return (serviceRequests || [])
      .filter(Boolean)
      .filter((sr: any) => {
        const inst = sr?.instantiatesCanonical;
        if (Array.isArray(inst)) return inst.length === 0 || !inst[0];
        return !inst;
      })
      .map((sr: any) => (sr?.id ?? '').toString())
      .filter((id) => !!id);
  }

  private pinServiceRequestsToCanonical$(serviceRequests: ServiceRequest[], canonical: string): Observable<any> {
    if (!canonical) return of(null);
    const idsToPin = this.getServiceRequestIdsMissingInstantiatesCanonical(serviceRequests);
    if (!idsToPin.length) return of(null);

    return forkJoin(
      idsToPin.map((id) =>
        this.updateServiceRequestInstantiatesCanonical$(id, canonical).pipe(
          catchError((e) => {
            // Fail the whole save to keep audit consistency.
            throw e;
          })
        )
      )
    );
  }

  private extractCreatedActivityDefinitionIdFromTransactionResponse(resp: any): string | null {
    const entries = Array.isArray(resp?.entry) ? resp.entry : [];

    // Prefer returned representation if available
    const createdByResource = entries
      .map((e: any) => e?.resource)
      .find((r: any) => r?.resourceType === this.activityDefinitionType && !!r?.id);
    if (createdByResource?.id) return createdByResource.id.toString();

    // Fallback to response.location
    const createdEntry = entries.find((e: any) => {
      const status = (e?.response?.status ?? '').toString();
      return status.startsWith('201') || status.startsWith('200');
    });
    const location = (createdEntry?.response?.location ?? '').toString();
    if (!location) return null;
    const match = location.match(/ActivityDefinition\/([^\/\?]+)/i);
    return match?.[1] ?? null;
  }

  onItemsRequiredUpdatedFromReview(event: any): void {
    if (!this.itemsRequiredEditEnabled) return;
    const rows = (Array.isArray(event?.itemsRequired)
      ? event.itemsRequired
      : (Array.isArray(event) ? event : [])).map((r: any) => ({
        items: this.utilityService.toCodeableConcept(r?.items),
        quantity: r?.quantity
      } as ItemsRequiredRow));

    const sanitized = this.sanitizeItemsRequiredRows(rows);
    if (sanitized.removedCount > 0) {
      this.snackBar.open(
        `Removed ${sanitized.removedCount} invalid item(s) (free-text). Please select from the list.`,
        'OK',
        { duration: 4000 }
      );
    }

    this.itemsRequiredRows = structuredClone(sanitized.rows);
    this.itemsRequiredDirty = JSON.stringify(this.itemsRequiredRows ?? []) !== JSON.stringify(this.originalItemsRequiredRows ?? []);
  }

  private sanitizeItemsRequiredRows(rows: ItemsRequiredRow[]): { rows: ItemsRequiredRow[]; removedCount: number } {
    const next: ItemsRequiredRow[] = [];
    let removed = 0;

    for (const row of rows ?? []) {
      const items: any = (row as any)?.items;
      const codings: any[] = Array.isArray(items?.coding) ? items.coding : [];

      // Reject free-text or {text}-only selections.
      const hasCode = codings.some((c) => !!(c?.code ?? '').toString().trim());
      if (!hasCode) {
        removed++;
        continue;
      }

      next.push(row);
    }

    return { rows: next, removedCount: removed };
  }

  saveReport(mode: 'draft' | 'final' | 'auto' = 'auto') {
    const srsInitial = this.getContextServiceRequests();
    const primaryInitial = srsInitial[0] ?? this.getContextServiceRequest();
    const patientIdInitial = this.resolvePatientIdFromContext(primaryInitial);

    // Always include all ServiceRequest ids this report represents (grouped + primary)
    const srIds = this.collectServiceRequestIdsForBasedOn(srsInitial);
    const srRefs = (srIds || []).map((id) => `ServiceRequest/${id}`);
    const resolveGroupServiceRequests$ = this.ensureServiceRequestsLoaded$(srsInitial, srIds);

    if (!this.isEditableV2) {
      this.errorService.openandCloseError('This report is not editable.');
      return;
    }

    // Ensure working DR is synced from the dynamic form when we're in form mode.
    if (this.diagnosticReportUiMode === 'form') {
      const values = this.formCmp?.aForm?.value ?? this.diagnosticReportFormValues;
      if (values) {
        this.diagnosticReportWorking = this.applySubmittedDataToDiagnosticReport(values, this.diagnosticReportWorking);
      }
    }

    const baseWorking: any = this.diagnosticReportWorking ?? { resourceType: 'DiagnosticReport' };
    const existingStatus = (baseWorking?.status ?? 'registered').toString().trim().toLowerCase();
    const desiredStatus = (mode === 'final')
      ? 'final'
      : (mode === 'draft' ? 'registered' : existingStatus);

    // Need patient context for payment checks when attempting final
    const mustBePaid = desiredStatus === 'final';

    const doSaveWithStatus = () => {
      resolveGroupServiceRequests$.subscribe((resolved) => {
        const srs = (resolved ?? []).length ? (resolved as ServiceRequest[]) : (srsInitial as ServiceRequest[]);
        const primary = srs[0] ?? primaryInitial;
        const patientId = this.resolvePatientIdFromContext(primary) ?? patientIdInitial;
        const subjectRef = (primary as any)?.subject ?? (patientId ? ({ reference: `Patient/${patientId}` } as any) : null);

        const payment$ = mustBePaid && patientId && srs.length
          ? forkJoin(srs.map((sr) => this.resolvePaymentBalancedForServiceRequest(sr, patientId)))
            .pipe(map((results) => ({ balanced: results.every((r) => r.balanced) })))
          : of({ balanced: true });

        payment$.subscribe(({ balanced }) => {
          if (mustBePaid && !balanced) {
            this.errorService.openandCloseError('Cannot submit a FINAL DiagnosticReport until payment is confirmed for all lab requests in this group (Invoice must be balanced).');
            return;
          }

          const proceedToSaveReport = () => {
            const base = structuredClone(baseWorking ?? {}) as any;

            const user: any = this.auth.user.getValue();
            const practitionerId = (user?.userId ?? user?.id ?? '').toString().trim();
            const performerRef = practitionerId ? ({ reference: `Practitioner/${practitionerId}`, display: user?.name ?? user?.display ?? undefined } as any) : null;

            const report: any = {
              ...base,
              resourceType: 'DiagnosticReport',
              status: desiredStatus,
              ...(subjectRef ? { subject: subjectRef } : {}),
              ...(srRefs.length ? { basedOn: srRefs.map((r) => ({ reference: r })) } : {}),
              ...(this.selectedObservationRefs.length ? { result: this.selectedObservationRefs.map((r) => ({ reference: r.reference, display: (r as any).display })) } : {}),
              ...((this.toReferenceResultsV2 === 'ImagingStudy')
                ? {
                  imagingStudy: (this.selectedImagingStudyRefs.length
                    ? this.selectedImagingStudyRefs.map((r) => ({ reference: r.reference, display: (r as any).display }))
                    : [])
                }
                : {}),
              ...(this.selectedSpecimenRefs.length ? { specimen: this.selectedSpecimenRefs.map((r) => ({ reference: r.reference, display: (r as any).display })) } : {}),
              ...(performerRef ? { performer: [performerRef] } : {}),
            } satisfies Partial<DiagnosticReport>;

            // Keep existing presentedForm (including any edited by ResourceDataReview)
            if (base?.presentedForm) {
              report.presentedForm = Array.isArray(base.presentedForm) ? base.presentedForm : [base.presentedForm];
            }

            const localPresentedForms = selectedImagesToDiagnosticReportPresentedForms(this.reportImages) || [];
            if (localPresentedForms.length) {
              report.presentedForm = [...(report.presentedForm || []), ...localPresentedForms];
            }

            // Auto-assign issued when finalizing (FHIR instant)
            if (desiredStatus === 'final') {
              const issued = (report as any)?.issued;
              if (!issued || (typeof issued === 'string' && issued.trim() === '')) {
                (report as any).issued = new Date().toISOString();
              }
            }

            const headers = { 'Content-Type': 'application/fhir+json', Accept: 'application/fhir+json', Prefer: 'return=representation' };

            // Prefer updating an existing placeholder report when one exists
            const existingId = (this.existingReport?.id ?? null) as string | null;

            // FINAL: do everything as a single transaction bundle (final report + SRs completed).
            if (desiredStatus === 'final') {
              this.saveFinalReportAndCompleteServiceRequestsAsBundle$(report as any, srIds, existingId).subscribe((saved) => {
                if (!saved) return;
                this.existingReport = saved as any;
                this.diagnosticReportWorking = structuredClone(saved as any);
                this.snackBar.open('DiagnosticReport finalized successfully.', 'OK', { duration: 3000 });
                this.isEditableV2 = false;
                this.diagnosticReportUiMode = 'review';
              });
              return;
            }

            // Draft/non-final: keep the lightweight single-resource save.
            const cleanedReport = this.cleanFhirResourceForSubmit(report);
            const req$ = existingId
              ? this.http.put<DiagnosticReport>(`${this.backendApiBaseUrl}/DiagnosticReport/${existingId}`, cleanedReport, { headers })
              : this.http.post<DiagnosticReport>(`${this.backendApiBaseUrl}/DiagnosticReport`, cleanedReport, { headers });

            req$.pipe(
              catchError((e: any) => {
                console.error(e);
                this.errorService.openandCloseError('Failed to save DiagnosticReport.');
                return of(null);
              })
            ).subscribe((saved: any) => {
              if (!saved) return;
              this.stateService.persistLocalResource(saved as any, 'saved');
              this.existingReport = saved as any;
              this.diagnosticReportWorking = structuredClone(saved as any);
              this.snackBar.open('DiagnosticReport saved successfully.', 'OK', { duration: 3000 });
              this.dialogRef?.close({ saved: true, resource: saved });
            });
          };

          const pinMissingInstantiatesCanonicalThenSave = (canonical: string | null) => {
            // Only pin when saving a FINAL report (audit hardening at finalization time).
            if (desiredStatus !== 'final') {
              proceedToSaveReport();
              return;
            }

            // Only attempt to pin when we're in a single-code context and the UI isn't in the mixed-code read-only mode.
            // (Mixed-code grouped reports intentionally do not support pinning/edits here.)
            if (!this.itemsRequiredEditEnabled) {
              proceedToSaveReport();
              return;
            }

            const distinctCodes = Array.from(new Set((srs || []).map((sr) => this.getServiceRequestCode(sr as any)).filter(Boolean) as string[]));
            if (distinctCodes.length !== 1) {
              proceedToSaveReport();
              return;
            }

            const idsToPin = this.getServiceRequestIdsMissingInstantiatesCanonical(srs);
            if (!idsToPin.length) {
              proceedToSaveReport();
              return;
            }

            const chosenCanonical$ = canonical
              ? of(canonical)
              : this.findActiveDefaultActivityDefinitionForCode$(distinctCodes[0]).pipe(
                map((def) => this.activityDefinitionToCanonical(def)),
                catchError(() => of(null))
              );

            chosenCanonical$.pipe(
              switchMap((c) => {
                if (!c) return of(null);
                return this.pinServiceRequestsToCanonical$(srs, c);
              })
            ).subscribe({
              next: () => proceedToSaveReport(),
              error: (e) => {
                console.error(e);
                this.errorService.openandCloseError('Failed to pin this ServiceRequest to the default configuration (instantiatesCanonical). Report was not saved.');
              }
            });
          };

          // If items required was edited, persist those changes BEFORE saving the report
          if (this.itemsRequiredDirty) {
            const distinctCodes = Array.from(
              new Set((srs || []).map((sr) => this.getServiceRequestCode(sr as any)).filter(Boolean) as string[])
            );

            if (distinctCodes.length > 1) {
              this.errorService.openandCloseError('Items Required edits can only be applied when all ServiceRequests in this report share the same service code.');
              return;
            }

            const srId = (primary as any)?.id?.toString?.() ?? '';
            if (!srId) {
              this.errorService.openandCloseError('Cannot persist items required changes: ServiceRequest id is missing.');
              return;
            }

            // If the resolved items required came from an instance override already,
            // default-vs-instance prompt is confusing: the SR is already pinned.
            // In that case, persist as instance again (replace the instance canonical).
            if (this.itemsRequiredSource === 'instance') {
              if (srs.length > 1) {
                this.errorService.openandCloseError('Editing an instance Items Required override is only supported for a single ServiceRequest. Open the ServiceRequest and confirm items used there.');
                return;
              }

              this.persistItemsRequiredForInstance$(primary as any, srId).subscribe({
                next: () => {
                  this.originalItemsRequiredRows = structuredClone(this.itemsRequiredRows);
                  this.itemsRequiredDirty = false;
                  proceedToSaveReport();
                },
                error: (err) => {
                  console.error(err);
                  this.errorService.openandCloseError('Failed to apply items required for this ServiceRequest.');
                }
              });
              return;
            }

            const dialogRef = this.dialog.open(ItemsRequiredApplyDialogComponent, {
              maxWidth: '680px',
              data: {
                title: 'Items required changed',
                message: 'Choose whether to apply this change as the new default for this service code, or apply only to this ServiceRequest.'
              }
            });

            dialogRef.afterClosed().subscribe((choice: ItemsRequiredApplyChoice) => {
              if (!choice || choice === 'cancel') {
                // user cancelled persistence; do not proceed to save report
                return;
              }

              if (choice === 'instance' && srs.length > 1) {
                this.errorService.openandCloseError('Apply-only-to-this-ServiceRequest is only supported when creating a report for a single ServiceRequest. Choose "Apply as default" or remove grouping.');
                return;
              }

              const selectedCode = this.getServiceRequestCode(primary as any) || '';
              if (!selectedCode) {
                this.errorService.openandCloseError('Cannot persist items required changes: ServiceRequest code is missing.');
                return;
              }

              if (choice === 'default') {
                this.persistItemsRequiredAsDefault$(primary as any, selectedCode).subscribe({
                  next: (createdCanonical: string | null) => {
                    this.originalItemsRequiredRows = structuredClone(this.itemsRequiredRows);
                    this.itemsRequiredDirty = false;
                    pinMissingInstantiatesCanonicalThenSave(createdCanonical);
                  },
                  error: (err) => {
                    console.error(err);
                    this.errorService.openandCloseError('Failed to apply items required as default.');
                  }
                });
                return;
              }

              // instance-only
              this.persistItemsRequiredForInstance$(primary as any, srId).subscribe({
                next: () => {
                  this.originalItemsRequiredRows = structuredClone(this.itemsRequiredRows);
                  this.itemsRequiredDirty = false;
                  // Instance-only persistence already pins this SR (instantiatesCanonical), so just save.
                  proceedToSaveReport();
                },
                error: (err) => {
                  console.error(err);
                  this.errorService.openandCloseError('Failed to apply items required for this ServiceRequest.');
                }
              });
            });
            return;
          }

          // No items-required edits: still pin missing instantiatesCanonical to the resolved default (audit safety).
          pinMissingInstantiatesCanonicalThenSave(null);
        });
      });
    };

    if (mode === 'final') {
      const practitionerId = this.resolveLoggedInPractitionerId();
      if (!practitionerId) {
        this.errorService.openandCloseError('Cannot finalize: your Practitioner id could not be resolved from your login profile. Please re-login and try again.');
        return;
      }

      this.resolvePractitionerLastNameFromBackend$(practitionerId).pipe(take(1)).subscribe((expectedLastNameRaw) => {
        const expected = expectedLastNameRaw ? this.normalizeNameToken(expectedLastNameRaw) : null;
        if (!expected) {
          this.errorService.openandCloseError('Cannot finalize: your last name could not be resolved from the server. Please re-login and try again.');
          return;
        }

        const confirmRef = this.dialog.open(FinalizeDiagnosticReportConfirmDialogComponent, {
          maxWidth: '560px',
          data: {
            expectedLastName: expectedLastNameRaw,
            title: 'Finalize DiagnosticReport',
            message: 'Finalizing will lock this report and mark all associated ServiceRequests as completed. Type your last name to confirm.'
          }
        });

        confirmRef.afterClosed().subscribe((typed: string | null) => {
          const provided = typed ? this.normalizeNameToken(typed) : '';
          if (!provided) return;
          if (provided !== expected) {
            this.errorService.openandCloseError('Last name does not match the record on the server. Finalization cancelled.');
            return;
          }
          doSaveWithStatus();
        });
      });
      return;
    }

    doSaveWithStatus();
  }

  cancelReport(): void {
    const existingId = (this.existingReport?.id ?? null) as string | null;
    if (!existingId) {
      this.errorService.openandCloseError('No saved DiagnosticReport found to cancel.');
      return;
    }

    const status = (this.existingReport as any)?.status?.toString?.().trim?.().toLowerCase?.() ?? '';
    if (status === 'final') {
      this.errorService.openandCloseError('Final DiagnosticReports cannot be cancelled.');
      return;
    }

    if (status === 'cancelled' || status === 'entered-in-error') {
      this.errorService.openandCloseError(`This report is already ${status}.`);
      return;
    }

    const confirmRef = this.dialog.open(ConfirmDialogComponent, {
      data: { message: 'Cancel this report? This will set status to CANCELLED.' }
    });

    confirmRef.afterClosed().subscribe((ok) => {
      if (!ok) return;
      const headers = { 'Content-Type': 'application/fhir+json', Accept: 'application/fhir+json', Prefer: 'return=representation' };
      const next: any = { ...(this.existingReport as any), status: 'cancelled' };
      this.http.put<DiagnosticReport>(`${this.backendApiBaseUrl}/DiagnosticReport/${existingId}`, next, { headers }).pipe(
        catchError((e: any) => {
          console.error(e);
          this.errorService.openandCloseError('Failed to cancel the DiagnosticReport.');
          return of(null);
        })
      ).subscribe((saved: any) => {
        if (!saved) return;
        this.stateService.persistLocalResource(saved as any, 'saved');
        this.snackBar.open('DiagnosticReport cancelled.', 'OK', { duration: 3000 });
        this.dialogRef?.close({ saved: true, resource: saved });
      });
    });
  }

  private buildNewActivityDefinitionFromBase(base: any, status: string): any {
    const next = structuredClone(base ?? {});
    // For POST: strip server-assigned keys
    if (next && typeof next === 'object') {
      delete next.id;
      delete next.meta;
    }
    next.resourceType = 'ActivityDefinition';
    next.status = status;
    if (!next.kind) next.kind = 'ServiceRequest';
    return next;
  }

  // private findActiveDefaultActivityDefinitionForCode$(code: string): Observable<any | null> {
  //   if (!code) return of(null);
  // return this.ensureActiveActivityDefinitionsLoaded$().pipe(
  //   map((defs) => {
  //     const matches = (defs || []).filter((d: any) => (d?.code as any)?.coding?.[0]?.code === code);
  //     const withTopic = matches.find((d: any) => (d?.topic || []).some((t: any) => ((t?.text ?? '').toString().toLowerCase()).includes('items usage computation')));
  //     return withTopic ?? matches[0] ?? null;
  //   })
  // );
  // }
  utilityService = inject(UtilityService);

  private readonly __DELETE_EMPTY = Symbol('DELETE_EMPTY_FHIR_FIELD');

  private cleanFhirResourceForSubmit<T>(resource: T): T {
    const cleaned = this.cleanValueForSubmit(resource) as any;
    return (cleaned === this.__DELETE_EMPTY ? resource : cleaned) as T;
  }

  private cleanValueForSubmit(value: any): any {
    if (value === null || value === undefined) return this.__DELETE_EMPTY;

    if (typeof value === 'string') {
      return value.trim() === '' ? this.__DELETE_EMPTY : value;
    }

    if (Array.isArray(value)) {
      const next = value
        .map((v) => this.cleanValueForSubmit(v))
        .filter((v) => v !== this.__DELETE_EMPTY);
      return next.length ? next : this.__DELETE_EMPTY;
    }

    if (typeof value === 'object') {
      const input = value as Record<string, any>;
      const out: Record<string, any> = {};

      for (const key of Object.keys(input)) {
        const v = input[key];
        if (v === null || v === undefined) continue;
        const cleaned = this.cleanValueForSubmit(v);
        if (cleaned === this.__DELETE_EMPTY) continue;
        out[key] = cleaned;
      }

      // Preserve critical FHIR keys if present on the input
      if (input['resourceType'] && !out['resourceType']) out['resourceType'] = input['resourceType'];
      if (input['id'] && !out['id']) out['id'] = input['id'];

      return Object.keys(out).length ? out : this.__DELETE_EMPTY;
    }

    // number | boolean | bigint | symbol | function: keep as-is
    return value;
  }

  private findActiveDefaultActivityDefinitionForCode$(code: string): Observable<any | null> {
    const normalized = (code ?? '').toString().trim();
    if (!normalized) return of(null);

    const pickBest = (defs: any[]): any | null => {
      const matches = (defs || []).filter((d: any) => {
        const codings = (d?.code as any)?.coding ?? [];
        return Array.isArray(codings) && codings.some((c: any) => (c?.code ?? '').toString().trim() === normalized);
      });

      const withTopic = matches.find((d: any) =>
        (d?.topic || []).some((t: any) =>
          ((t?.text ?? '').toString().toLowerCase()).includes('items usage computation')
        )
      );

      return withTopic ?? matches[0] ?? null;
    };

    // First try whatever is already loaded/cached
    // return this.ensureActiveActivityDefinitionsLoaded$().pipe(
    //   switchMap((cached) => {
    // const fromCache = pickBest(cached);
    // if (fromCache) return of(fromCache);

    // If not found, do a three-tier query (still do NOT include code in urlSearchObject)
    return this.utilityService.threeTierQueryAndSyncToStateOnce$<any>({
      resourceType: 'ActivityDefinition',
      urlSearchObject: {
        status: 'active',
      },
      querySearchObject: [
        { key: 'status', value: 'active' },
        { key: 'code.coding.code', value: normalized },
        { key: 'topic.text', value: 'items usage computation' }
      ],
      scope: 'orgWide',
      // count: 2000,
      tier1FollowNextLinks: true,
      tier1MaxPages: 10,
      expectedMinLength: 1,
      tier3TriggerWhenBelowExpectedMinOnTier1Failure: true,
    }).pipe(
      // tap((resources: any[]) => {
      //   // Refresh cache (best-effort). This also helps reduce accidental “duplicate active defaults”.
      //   this.stateService.orgWideResources.activityDefinitions.next(
      //     (resources || []).map((d: any) => ({
      //       actualResource: d,
      //       referenceId: d?.id ? `ActivityDefinition/${d.id}` : null,
      //       savedStatus: 'saved',
      //     }))
      //   );
      // }),
      map((fresh) => pickBest(fresh)),
      catchError(() => of(null)),
      //   );
      // }),
      catchError(() => of(null))
    );
  }


  private persistItemsRequiredAsDefault$(serviceRequest: ServiceRequest, code: string): Observable<string | null> {
    const baseForNew = this.resolvedActivityDefinition ?? {};

    return this.findActiveDefaultActivityDefinitionForCode$(code).pipe(
      switchMap((existingDefault) => {
        const newActiveBase = this.buildNewActivityDefinitionFromBase(baseForNew, 'active');
        const newActive = applyItemsRequiredRowsToActivityDefinition(newActiveBase, this.itemsRequiredRows, {
          preserveUnitsFrom: existingDefault ?? baseForNew,
          backendSystemFallback: this.backendApiBaseUrl
        });

        // Ensure code stays aligned to SR code
        const srCoding = (serviceRequest as any)?.code?.coding?.[0];
        if (srCoding?.code) {
          (newActive as any).code = {
            coding: [{
              code: srCoding.code,
              display: srCoding.display,
              system: srCoding.system
            }],
            text: (serviceRequest as any)?.code?.text ?? srCoding.display ?? srCoding.code
          };
        }

        const entries: any[] = [
          { request: { method: 'POST', url: 'ActivityDefinition' }, resource: this.cleanFhirResourceForSubmit(newActive) }
        ];

        if (existingDefault?.id) {
          entries.push({
            request: { method: 'PUT', url: `ActivityDefinition/${existingDefault.id}` },
            resource: this.cleanFhirResourceForSubmit({ ...existingDefault, status: 'retired' })
          });
        }

        const bundle: Bundle = { resourceType: 'Bundle', type: 'transaction', entry: entries };
        return this.http.post<Bundle>(this.backendApiBaseUrl, bundle, {
          headers: {
            'Content-Type': 'application/fhir+json',
            Accept: 'application/fhir+json',
            Prefer: 'return=representation'
          }
        }).pipe(
          tap((resp: any) => {
            if (resp) this.stateService.processOrgWideBundleTransaction(resp as any);
          }),
          map((resp: any) => {
            const createdId = this.extractCreatedActivityDefinitionIdFromTransactionResponse(resp);
            if (!createdId) return null;
            const base = this.backendApiBaseUrl.replace(/\/+$/, '');
            return `${base}/ActivityDefinition/${createdId}`;
          })
        );
      })
    );
  }

  private persistItemsRequiredForInstance$(serviceRequest: ServiceRequest, serviceRequestId: string): Observable<any> {
    const baseForNew = this.resolvedActivityDefinition ?? {};
    const newRetiredBase = this.buildNewActivityDefinitionFromBase(baseForNew, 'retired');

    const newRetired = applyItemsRequiredRowsToActivityDefinition(newRetiredBase, this.itemsRequiredRows, {
      preserveUnitsFrom: baseForNew,
      backendSystemFallback: this.backendApiBaseUrl
    });

    const headers = { 'Content-Type': 'application/fhir+json', Accept: 'application/fhir+json', Prefer: 'return=representation' };

    const cleaned = this.cleanFhirResourceForSubmit(newRetired);
    return this.http.post<any>(`${this.backendApiBaseUrl}/ActivityDefinition`, cleaned, { headers }).pipe(
      switchMap((created: any) => {
        const adId = created?.id?.toString?.() ?? '';
        if (!adId) {
          throw new Error('ActivityDefinition was created but has no id.');
        }

        this.stateService.persistOrgWideResource(created as any, 'saved');

        const canonical = `${this.backendApiBaseUrl.replace(/\/+$/, '')}/ActivityDefinition/${adId}`;
        return this.updateServiceRequestInstantiatesCanonical$(serviceRequestId, canonical, { overwrite: true }).pipe(
          tap(() => {
            // keep UI in sync for subsequent edits
            this.itemsRequiredSource = 'instance';
            this.resolvedActivityDefinition = created;
          })
        );
      })
    );
  }

  private updateServiceRequestInstantiatesCanonical$(
    serviceRequestId: string,
    canonical: string,
    options?: { overwrite?: boolean }
  ): Observable<any> {
    if (!serviceRequestId) return of(null);
    const headers = { 'Content-Type': 'application/fhir+json', Accept: 'application/fhir+json', Prefer: 'return=representation' };

    return this.http.get<any>(`${this.backendApiBaseUrl}/ServiceRequest/${serviceRequestId}`, { headers: { Accept: 'application/fhir+json' } }).pipe(
      switchMap((sr: any) => {
        if (!sr || sr?.resourceType === 'OperationOutcome') {
          throw new Error('Failed to fetch ServiceRequest before linking instantiatesCanonical.');
        }
        const existing = sr?.instantiatesCanonical;
        const alreadyPinned = Array.isArray(existing) ? !!existing[0] : !!existing;
        if (alreadyPinned && !options?.overwrite) {
          return of(sr);
        }
        const next = { ...sr, instantiatesCanonical: [canonical] };
        return this.http.put<any>(`${this.backendApiBaseUrl}/ServiceRequest/${serviceRequestId}`, next, { headers });
      }),
      tap((updated: any) => {
        if (updated && updated.resourceType === 'ServiceRequest') {
          this.stateService.persistLocalResource(updated as any, 'saved');
          this.stateService.persistOrgWideResource(updated as any, 'saved');
        }
      })
    );
  }

  private resolvePaymentBalancedForServiceRequest(serviceRequest: ServiceRequest, patientId: string): Observable<{ balanced: boolean }> {
    const srId = (serviceRequest?.id ?? '').toString().trim();
    if (!srId || !patientId) return of({ balanced: false });

    const type = inferServiceRequestType(serviceRequest);

    if (type === 'imaging') {
      return this.billingPlaceholder.resolveBillingAndPlaceholderForImagingStudy(serviceRequest, patientId).pipe(
        map((r) => ({ balanced: r.hasBalancedInvoice })),
        catchError(() => of({ balanced: false }))
      );
    }

    if (type === 'procedure' || type === 'surgery') {
      return this.billingPlaceholder.resolveBillingAndPlaceholderForProcedure(serviceRequest, patientId).pipe(
        map((r) => ({ balanced: r.hasBalancedInvoice })),
        catchError(() => of({ balanced: false }))
      );
    }

    if (type === 'laboratory') {
      return this.billingPlaceholder.resolveBillingAndPlaceholderForLab(serviceRequest, patientId).pipe(
        map((r) => ({ balanced: r.hasBalancedInvoice })),
        catchError(() => of({ balanced: false }))
      );
    }

    // Fallback: generic SR billing resolver
    return this.serviceRequestBillingPlaceholder.resolvePaymentForServiceRequest(serviceRequest, patientId).pipe(
      map((r) => ({ balanced: r.hasBalancedInvoice })),
      catchError(() => of({ balanced: false }))
    );
  }

  // utilityService = inject(UtilityService);
  toPassLabForm?: FormFields[]
}
